import React, { useEffect, useState, useCallback, useMemo, useRef } from "react";
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import listPlugin from '@fullcalendar/list';
import interactionPlugin from '@fullcalendar/interaction';
import { format, parse, addHours, isWithinInterval, startOfMonth, endOfMonth } from "date-fns";
import { db, auth } from '../../firebase.init';
import { collection, getDocs, addDoc, updateDoc, deleteDoc, doc, query, orderBy, limit, where, getDoc, serverTimestamp, arrayUnion, arrayRemove } from "firebase/firestore";
import { useAuthState } from 'react-firebase-hooks/auth';
import dayjs from "dayjs";
import './Calendar.css';
import { Loader } from "lucide-react";
import CreatableSelect from 'react-select/creatable';
import Select from 'react-select';
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import Flatpickr from "react-flatpickr";
import "flatpickr/dist/themes/material_red.css";
import QRCode from "qrcode";
import { useUserRole } from '../../hooks/useUserRole';
import UserEventModal from "./UserEventModal";
import { Modal, Button } from "react-bootstrap";
import ReactModal from "react-modal";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const eventColors = {
    general: { bg: '#FFD6D6', border: '#FF8A80', text: '#800000', label: 'General' },
    inspection: { bg: '#D0FFD6', border: '#66FF80', text: '#004400', label: 'Inspection' },
    remedial: { bg: '#D6E0FF', border: '#8090FF', text: '#000080', label: 'Remedial Work' },
    meeting: { bg: '#FFF2CC', border: '#FFD633', text: '#7A5900', label: 'Meeting' },
    site_visit: { bg: '#FFE0CC', border: '#FF9933', text: '#663300', label: 'Site Visit' },
    report_review: { bg: '#E6CCFF', border: '#B266FF', text: '#330066', label: 'Report Review' },
    communal: { bg: '#CCFFFF', border: '#66E0FF', text: '#00334C', label: 'Communal' },
    residential: { bg: '#FFE6F2', border: '#FF99CC', text: '#660033', label: 'Residential' }
};

// const inspectorColors = [
//     "#0095f8",
//     "#ff8e2c",
//     "#da0258",
//     "#05b605",
//     "#00FFFF",
// ];

// const carpenterColors = [
//     "#0047AB",
//     "#14b7e9",
//     "#1B4332",
//     "#005F73",
//     "#00FF7F",
// ];

// const directorColors = [
//     "#6A0DAD",
//     "#F72585",
//     "#CCFF00",
//     "#4B0082",
//     "#000000",
// ];

// const inspectorColorMap = {};
// const carpenterColorMap = {};
// const directorColorMap = {};

const CALENDAR_OWNER_UID = "rxaqnUdohkQBw3ZiH77vl2Eu4rT2";
export const CALENDAR_COLLECTION_PATH = ["users", CALENDAR_OWNER_UID, "calendar"];
const calendarCollection = () => collection(db, ...CALENDAR_COLLECTION_PATH);

export const dateUtils = {
    formatToDatabase: (date) => format(date, "dd/MM/yyyy HH:mm:ss"),
    parseFromDatabase: (dateString) => {
        if (!dateString) return new Date();
        try {
            const [datePart, timePart] = dateString.split(' ');
            const [day, month, year] = datePart.split('/');
            const [hours, minutes, seconds] = timePart.split(':');
            return new Date(year, month - 1, day, hours, minutes, seconds);
        } catch (error) {
            return new Date();
        }
    },
    toDateTimeLocal: (date) => format(date, "yyyy-MM-dd'T'HH:mm"),
    fromDateTimeLocal: (datetimeString) => parse(datetimeString, "yyyy-MM-dd'T'HH:mm", new Date()),
    formatForDisplay: (date) => format(date, "EEE, MMM d, yyyy 'at' h:mm a"),
    addMinutes: (date, minutes) => {
        const newDate = new Date(date);
        newDate.setMinutes(newDate.getMinutes() + Number(minutes));
        return newDate;
    }
};

const canWriteCalendar = (role) =>
    role === 'admin' || role === 'manager' || role === 'inspection' || role === 'coordinator' || role === 'remedial';


export const useCalendarEvents = (user) => {
    const [events, setEvents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const userData = useUserRole();

    const fetchCalendarEvents = useCallback(async () => {
        if (!user?.uid) {
            setLoading(false);
            return;
        }
        if (!userData.ready) return;

        try {
            setError(null);
            setLoading(true);

            const eventsQuery = query(calendarCollection(), orderBy("start", "asc"));
            const snapshot = await getDocs(eventsQuery);

            let fetched = snapshot.docs.map((docSnap) => {
                const data = docSnap.data();
                return {
                    id: docSnap.id,
                    title: data.title || `${formatTimeOnlyFromDbString(data.start)} • ${data.buildingAddress}`,
                    description: data.description || "",
                    type: data.type || "general",
                    category: data.category || data.type || "general",
                    buildingEmail: data.buildingEmail || "",
                    start: dateUtils.parseFromDatabase(data.start),
                    end: dateUtils.parseFromDatabase(data.end),

                    buildingAddress: data.buildingAddress || "",
                    buildingCity: data.buildingCity || "",
                    travelDistanceMiles: data.travelDistanceMiles || 0,
                    travelTimeMinutes: data.travelTimeMinutes || 0,
                    surveyTimeMinutes: data.surveyTimeMinutes || 0,
                    totalResiDoors: data.totalResiDoors || 0,
                    totalCommunalDoors: data.totalCommunalDoors || 0,
                    preventRescheduling: data.preventRescheduling || false,
                    inspectorName: data.inspectorName || '',
                    directorName: data.directorName || '',
                    carpenterName: data.carpenterName || '',
                    lunchDuration: data.lunchDuration || '',
                    postcode: data.postcode || '',
                    assignedInspector: data.assignedInspector || '',
                    assignedCarpenter: data.assignedCarpenter || '',
                    assignedDirector: data.assignedDirector || '',
                    doorUrl: data.doorUrl || '',
                    materialCost: data.materialCost || '',
                    jobPrice: data.jobPrice || '',
                    labourCost: data.labourCost || '',
                    ticket_id: data.ticket_id || null,
                };
            });

            if (userData.role === 'admin' || userData.role === 'manager' || userData.role === 'inspection' || userData.role === 'coordinator' || userData.role === 'remedial' || userData.role === 'reviewer') {
            } else if (userData.role === 'inspector') {
                fetched = fetched.filter(event => event.assignedInspector === userData.uid);
            } else if (userData.role === 'carpenter') {
                fetched = fetched.filter(event => event.assignedCarpenter === userData.uid);
            } else if (userData.role === 'director') {
                fetched = fetched.filter(event => event.assignedDirector === userData.uid);
            } else if (userData.role === 'client') {
                fetched = fetched.filter(event => event.buildingEmail === user.email);
            } else {
                fetched = [];
            }

            setEvents(fetched);
        } catch (err) {
            console.error("Error fetching events:", err);
            setError("Failed to load calendar events. Please try again.");
        } finally {
            setLoading(false);
        }
    }, [user, userData]);

    useEffect(() => {
        if (!userData.ready) return;
        fetchCalendarEvents();
    }, [userData.ready, userData]);

    const addEvent = useCallback(async (eventData) => {
        if (!canWriteCalendar(userData.role)) {
            throw new Error("You do not have permission to create events.");
        }

        try {
            const eventDataForStorage = {
                title: eventData.title,
                description: eventData.description,
                type: eventData.type,
                category: eventData.category || eventData.type,
                buildingEmail: eventData.buildingEmail,
                start: dateUtils.formatToDatabase(eventData.start),
                end: dateUtils.formatToDatabase(eventData.end),
                updatedAt: dateUtils.formatToDatabase(new Date()),
                createdAt: dateUtils.formatToDatabase(new Date()),

                buildingAddress: eventData.buildingAddress || '',
                buildingCity: eventData.buildingCity || '',
                travelDistanceMiles: eventData.travelDistanceMiles || 0,
                travelTimeMinutes: eventData.travelTimeMinutes || 0,
                surveyTimeMinutes: eventData.surveyTimeMinutes || 0,
                totalCommunalDoors: eventData.totalCommunalDoors || 0,
                totalResiDoors: eventData.totalResiDoors || 0,
                preventRescheduling: eventData.preventRescheduling || false,
                inspectorName: eventData.inspectorName || '',
                directorName: eventData.directorName || '',
                carpenterName: eventData.carpenterName || '',
                lunchDuration: eventData.lunchDuration || '',
                postcode: eventData.postcode || '',
                assignedInspector: eventData.assignedInspector || '',
                assignedCarpenter: eventData.assignedCarpenter || '',
                assignedDirector: eventData.assignedDirector || '',
                doorUrl: eventData.doorUrl || '',
                materialCost: eventData.materialCost || '',
                jobPrice: eventData.jobPrice || '',
                labourCost: eventData.labourCost || '',
                ticket_id: eventData.ticket_id || null,
            };
            const docRef = await addDoc(calendarCollection(), eventDataForStorage);
            const newEvent = {
                id: docRef.id,
                ...eventData,
                category: eventData.category || eventData.type
            };
            setEvents(prev => [...prev, newEvent]);
            return newEvent;
        } catch (error) {
            throw new Error(`Failed to create event: ${error.message}`);
        }
    }, [user, userData]);


    const copyEvent = useCallback(
        async (eventData, newDates) => {
            if (!canWriteCalendar(userData?.role)) {
                throw new Error(
                    "You do not have permission to create events."
                );
            }

            if (!eventData) {
                throw new Error("No event selected.");
            }

            const isMultiple = Array.isArray(newDates);
            const dates = [
                ...new Set(isMultiple ? newDates : [newDates]),
            ];

            if (!dates.length || dates.some((date) => !date)) {
                throw new Error("Please select at least one date.");
            }

            const toDate = (value) => {
                const date =
                    typeof value?.toDate === "function"
                        ? value.toDate()
                        : value == null
                            ? new Date(NaN)
                            : new Date(value);

                if (
                    !(date instanceof Date) ||
                    Number.isNaN(date.getTime())
                ) {
                    throw new Error("Invalid event date.");
                }

                return date;
            };

            const originalStart = toDate(eventData.start);
            const originalEnd = toDate(eventData.end);

            if (originalEnd < originalStart) {
                throw new Error(
                    "The event end cannot be before its start."
                );
            }

            const tomorrow = new Date();
            tomorrow.setHours(0, 0, 0, 0);
            tomorrow.setDate(tomorrow.getDate() + 1);

            const daySpan = Math.round(
                (
                    Date.UTC(
                        originalEnd.getFullYear(),
                        originalEnd.getMonth(),
                        originalEnd.getDate()
                    ) -
                    Date.UTC(
                        originalStart.getFullYear(),
                        originalStart.getMonth(),
                        originalStart.getDate()
                    )
                ) / 86400000
            );

            const applyTime = (day, originalTime) => {
                const result = new Date(day);

                result.setHours(
                    originalTime.getHours(),
                    originalTime.getMinutes(),
                    originalTime.getSeconds(),
                    originalTime.getMilliseconds()
                );

                if (
                    result.getFullYear() !== day.getFullYear() ||
                    result.getMonth() !== day.getMonth() ||
                    result.getDate() !== day.getDate() ||
                    result.getHours() !== originalTime.getHours() ||
                    result.getMinutes() !== originalTime.getMinutes()
                ) {
                    throw new Error(
                        "An event time does not exist on one of the selected dates in your local timezone."
                    );
                }

                return result;
            };

            const {
                id,
                createdAt,
                updatedAt,
                ...eventWithoutId
            } = eventData;

            const copies = dates.map((dateString) => {
                if (
                    typeof dateString !== "string" ||
                    !/^\d{4}-\d{2}-\d{2}$/.test(dateString)
                ) {
                    throw new Error("Please select a valid date.");
                }

                const [year, month, day] = dateString
                    .split("-")
                    .map(Number);

                const selectedDate = new Date(
                    `${dateString}T12:00:00`
                );

                if (
                    year < 1000 ||
                    Number.isNaN(selectedDate.getTime()) ||
                    selectedDate.getFullYear() !== year ||
                    selectedDate.getMonth() !== month - 1 ||
                    selectedDate.getDate() !== day
                ) {
                    throw new Error(
                        `Invalid copy date: ${dateString}`
                    );
                }

                if (selectedDate < tomorrow) {
                    throw new Error(
                        "Copy dates must be tomorrow or later."
                    );
                }

                const endDate = new Date(selectedDate);
                endDate.setDate(endDate.getDate() + daySpan);

                const copiedStart = applyTime(
                    selectedDate,
                    originalStart
                );

                const copiedEnd = applyTime(
                    endDate,
                    originalEnd
                );

                if (copiedEnd < copiedStart) {
                    throw new Error(
                        "The copied event end cannot be before its start."
                    );
                }

                return {
                    date: dateString,
                    data: {
                        ...eventWithoutId,
                        start: copiedStart,
                        end: copiedEnd,
                    },
                };
            });

            const result = {
                created: [],
                failed: [],
            };

            for (const copy of copies) {
                try {
                    const newEvent = await addEvent(copy.data);

                    result.created.push({
                        date: copy.date,
                        event: newEvent,
                    });
                } catch (error) {
                    result.failed.push({
                        date: copy.date,
                        error:
                            error?.message ||
                            "Failed to copy event.",
                    });
                }
            }

            if (!isMultiple) {
                if (result.failed.length) {
                    throw new Error(
                        `Failed to copy event: ${result.failed[0].error}`
                    );
                }

                return result.created[0].event;
            }

            return result;
        },
        [addEvent, userData?.role]
    );

    const updateEvent = useCallback(async (eventId, eventData) => {
        if (!canWriteCalendar(userData.role)) {
            throw new Error("You do not have permission to update events.");
        }

        try {
            const eventDataForStorage = {
                title: eventData.title || '',
                description: eventData.description || '',
                type: eventData.type || 'general',
                category: eventData.category || eventData.type || 'general',
                buildingEmail: eventData.buildingEmail || '',
                start: dateUtils.formatToDatabase(eventData.start),
                end: dateUtils.formatToDatabase(eventData.end),
                updatedAt: dateUtils.formatToDatabase(new Date()),

                buildingAddress: eventData.buildingAddress || '',
                buildingCity: eventData.buildingCity || '',
                travelDistanceMiles: eventData.travelDistanceMiles || 0,
                travelTimeMinutes: eventData.travelTimeMinutes || 0,
                surveyTimeMinutes: eventData.surveyTimeMinutes || 0,
                totalCommunalDoors: eventData.totalCommunalDoors || 0,
                totalResiDoors: eventData.totalResiDoors || 0,
                preventRescheduling: eventData.preventRescheduling || false,
                inspectorName: eventData.inspectorName || '',
                carpenterName: eventData.carpenterName || '',
                directorName: eventData.directorName || '',
                lunchDuration: eventData.lunchDuration || '',
                postcode: eventData.postcode || '',
                assignedInspector: eventData.assignedInspector || '',
                assignedCarpenter: eventData.assignedCarpenter || '',
                assignedDirector: eventData.assignedDirector || '',
                doorUrl: eventData.doorUrl || '',
                materialCost: eventData.materialCost || '',
                jobPrice: eventData.jobPrice || '',
                labourCost: eventData.labourCost || '',
            };

            Object.keys(eventDataForStorage).forEach(key => {
                if (eventDataForStorage[key] === undefined) {
                    delete eventDataForStorage[key];
                }
            });

            await updateDoc(doc(db, ...CALENDAR_COLLECTION_PATH, eventId), eventDataForStorage);

            setEvents(prev => prev.map(e => e.id === eventId ? {
                ...e,
                ...eventData,
                category: eventData.category || eventData.type || e.category
            } : e));
            return true;
        } catch (error) {
            throw new Error(`Failed to update event: ${error.message}`);
        }
    }, [user, userData]);

    const deleteEvent = useCallback(async (eventId, ticketIdOverride) => {
        if (!canWriteCalendar(userData.role)) {
            throw new Error("You do not have permission to delete events.");
        }

        try {
            const linkedTicketId = ticketIdOverride ?? events.find(e => e.id === eventId)?.ticket_id ?? null;

            await deleteDoc(doc(db, ...CALENDAR_COLLECTION_PATH, eventId));
            setEvents(prev => prev.filter(e => e.id !== eventId));

            if (linkedTicketId) {
                updateDoc(doc(db, "tickets", linkedTicketId), {
                    schedule_ids: arrayRemove(eventId),
                    updated_at: serverTimestamp(),
                }).catch((err) => console.error("Failed to clear schedule link on ticket:", err));
            }
        } catch (error) {
            throw new Error(`Failed to delete event: ${error.message}`);
        }
    }, [user, userData, events]);

    return { events, loading, error, setError, addEvent, copyEvent, updateEvent, deleteEvent, refetch: fetchCalendarEvents };
};

function formatTimeOnlyFromDbString(dateStr) {
    const [, timePart] = dateStr.split(" ");
    const [hourStr, minuteStr] = timePart.split(":");

    let hour = parseInt(hourStr, 10);
    const ampm = hour >= 12 ? "PM" : "AM";

    if (hour === 0) hour = 12;
    else if (hour > 12) hour -= 12;

    const formattedHour = String(hour).padStart(2, "0");

    return `${formattedHour}:${minuteStr} ${ampm}`;
}

const CalendarPage = () => {
    const [user, loadingAuth] = useAuthState(auth);
    const [selectedEvent, setSelectedEvent] = useState(null);
    const [showEventModal, setShowEventModal] = useState(false);
    const [view, setView] = useState('dayGridMonth');
    const [selectedSlot, setSelectedSlot] = useState(null);
    const [currentDate, setCurrentDate] = useState(new Date());
    const [isMobile, setIsMobile] = useState(false);
    const { events, loading, error, setError, addEvent, copyEvent, updateEvent, deleteEvent } = useCalendarEvents(user);
    const calendarRef = useRef(null);
    const [isAdmin, setIsAdmin] = useState(false);
    const [loadingSchedule, setLoadingScheudle] = useState(false);

    const [selectedUserEvent, setSelectedUserEvent] = useState(null);
    const [showUserEventModal, setShowUserEventModal] = useState(false);

    const [showExportModal, setShowExportModal] = useState(false);
    const [inspectores, setInspectores] = useState([]);
    const [inspectorColorMap, setInspectorColorMap] = useState({});
    const [carpenters, setCarpenters] = useState([]);
    const [carpenterColorMap, setCarpenterColorMap] = useState({});
    const [directors, setDirectors] = useState([]);
    const [directorColorMap, setDirectorColorMap] = useState({});
    const [selectedInspector, setSelectedInspector] = useState("");
    const [selectedDirector, setSelectedDirector] = useState("");
    const [selectedCarpenter, setSelectedCarpenter] = useState("");
    const [exportLoading, setExportLoading] = useState(false);
    const [startDate, setStartDate] = useState(null);
    const [endDate, setEndDate] = useState(null);
    const [filteredEvents, setFilteredEvents] = useState([]);

    const [searchQuery, setSearchQuery] = useState('');
    const [searchField, setSearchField] = useState('');

    const userData = useUserRole();

    useEffect(() => {
        const defaultField = searchOptions.includes('all') ? 'all' : searchOptions[0];
        setSearchField(defaultField);
    }, [userData.role]);

    useEffect(() => {
        if ((selectedInspector || selectedCarpenter || selectedDirector) && startDate && endDate) {
            const f = (events || []).filter(ev => {
                const evStart = parseDateTime(ev.start);
                return (
                    evStart >= startDate &&
                    evStart <= endDate &&
                    (
                        ev.assignedInspector === selectedInspector ||
                        ev.assignedCarpenter === selectedCarpenter ||
                        ev.assignedDirector === selectedDirector
                    )
                );
            });
            setFilteredEvents(f);
        } else {
            setFilteredEvents([]);
        }
    }, [selectedInspector, selectedCarpenter, selectedDirector, startDate, endDate, events, userData.role]);

    useEffect(() => {
        if (!user?.email) return;
        if (!userData.ready) return;

        const admin = userData.role === 'admin' ||
            userData.role === 'manager' ||
            userData.role === 'inspection' ||
            userData.role === 'coordinator' ||
            userData.role === 'remedial' ||
            userData.role === 'reviewer';
        setIsAdmin(admin);

    }, [user, userData]);

    useEffect(() => {
        const checkMobile = () => {
            const mobile = window.innerWidth < 768;
            setIsMobile(mobile);
            if (mobile && view === 'dayGridMonth') {
                setView('listWeek');
            }
        };
        checkMobile();
        window.addEventListener('resize', checkMobile);
        return () => window.removeEventListener('resize', checkMobile);
    }, [view]);

    const openCreateEventModal = useCallback((prefillData = null, slot = null) => {
        setSelectedEvent(
            prefillData && Object.keys(prefillData).length
                ? { ...prefillData, id: undefined }
                : null
        );
        setSelectedSlot(slot || { start: new Date(), end: addHours(new Date(), 1) });
        setShowEventModal(true);
    }, []);

    const openEditEventModal = useCallback((eventData) => {
        if (!eventData?.id) {
            console.warn("openEditEventModal called without an id — use openCreateEventModal instead");
            return;
        }
        setSelectedEvent(eventData);
        setSelectedSlot(null);
        setShowEventModal(true);
    }, []);

    const closeEventModal = useCallback(() => {
        setShowEventModal(false);
        setSelectedEvent(null);
        setSelectedSlot(null);
    }, []);

    const handleDateSelect = useCallback(
        (selectInfo) => {
            if (!user?.uid) {
                setError("Please sign in to create events.");
                return;
            }

            const start = selectInfo.start;

            const today = new Date();
            today.setHours(0, 0, 0, 0);

            const selectedDate = new Date(start);
            selectedDate.setHours(0, 0, 0, 0);

            if (selectedDate < today) {
                setError("You can only create events for future dates.");
                return;
            }

            const end =
                selectInfo.end || addHours(start, 1);

            openCreateEventModal(null, {
                start,
                end,
            });
        },
        [
            user,
            setError,
            openCreateEventModal,
        ]
    );

    const handleEventClick = useCallback(
        (clickInfo) => {
            const eventData = {
                id: clickInfo.event.id,
                title: clickInfo.event.title,
                start: clickInfo.event.start,
                end: clickInfo.event.end,

                description:
                    clickInfo.event.extendedProps?.description || "",

                type:
                    clickInfo.event.extendedProps?.type || "",

                category:
                    clickInfo.event.extendedProps?.category || "",

                buildingEmail:
                    clickInfo.event.extendedProps?.buildingEmail || "",

                buildingAddress:
                    clickInfo.event.extendedProps?.buildingAddress || "",

                buildingCity:
                    clickInfo.event.extendedProps?.buildingCity || "",

                travelDistanceMiles:
                    clickInfo.event.extendedProps?.travelDistanceMiles || "",

                travelTimeMinutes:
                    clickInfo.event.extendedProps?.travelTimeMinutes || "",

                surveyTimeMinutes:
                    clickInfo.event.extendedProps?.surveyTimeMinutes || "",

                totalCommunalDoors:
                    clickInfo.event.extendedProps?.totalCommunalDoors ?? "",

                totalResiDoors:
                    clickInfo.event.extendedProps?.totalResiDoors ?? "",

                inspectorName:
                    clickInfo.event.extendedProps?.inspectorName || "",

                directorName:
                    clickInfo.event.extendedProps?.directorName || "",

                carpenterName:
                    clickInfo.event.extendedProps?.carpenterName || "",

                lunchDuration:
                    clickInfo.event.extendedProps?.lunchDuration || "",

                postcode:
                    clickInfo.event.extendedProps?.postcode || "",

                assignedInspector:
                    clickInfo.event.extendedProps?.assignedInspector || "",

                assignedCarpenter:
                    clickInfo.event.extendedProps?.assignedCarpenter || "",

                assignedDirector:
                    clickInfo.event.extendedProps?.assignedDirector || "",

                doorUrl:
                    clickInfo.event.extendedProps?.doorUrl || "",
            };

            if (userData.role === "client") {
                setSelectedUserEvent(eventData);
                setShowUserEventModal(true);
                return;
            }

            openEditEventModal(eventData);
        },
        [userData.role, openEditEventModal]
    );

    const handleEventChange = useCallback((changeInfo) => {
        const event = changeInfo.event;
        const eventData = {
            start: event.start,
            end: event.end,
            title: event.title,
            description: event.extendedProps?.description || '',
            type: event.extendedProps?.type || 'general',
            category: event.extendedProps?.category || event.extendedProps?.type || 'general',
            buildingEmail: event.extendedProps?.buildingEmail || '',

            buildingAddress: event.extendedProps?.buildingAddress || '',
            buildingCity: event.extendedProps?.buildingCity || '',
            travelDistanceMiles: event.extendedProps?.travelDistanceMiles,
            travelTimeMinutes: event.extendedProps?.travelTimeMinutes,
            surveyTimeMinutes: event.extendedProps?.surveyTimeMinutes,
            totalCommunalDoors: event.extendedProps?.totalCommunalDoors,
            totalResiDoors: event.extendedProps?.totalResiDoors,
            preventRescheduling: event.extendedProps?.preventRescheduling || false,
            inspectorName: event.extendedProps?.inspectorName || '',
            directorName: event.extendedProps?.directorName || '',
            carpenterName: event.extendedProps?.carpenterName || '',
            lunchDuration: event.extendedProps?.lunchDuration || '',
            postcode: event.extendedProps?.postcode || '',
            doorUrl: event.extendedProps?.doorUrl || '',
            materialCost: event.extendedProps?.materialCost || '',
            jobPrice: event.extendedProps?.jobPrice || '',
            labourCost: event.extendedProps?.labourCost || '',

            assignedInspector: event.extendedProps?.assignedInspector || '',
            assignedCarpenter: event.extendedProps?.assignedCarpenter || '',
            assignedDirector: event.extendedProps?.assignedDirector || '',
        };
        updateEvent(event.id, eventData);
    }, [updateEvent]);

    const handleSaveEvent = useCallback(async (eventData) => {
        try {
            setError(null);
            const eventDataWithCategory = {
                ...eventData,
                category: eventData.category || eventData.type
            };

            if (selectedEvent?.id) {
                await updateEvent(selectedEvent.id, eventDataWithCategory);
            } else {
                await addEvent(eventDataWithCategory);
            }
            closeEventModal();
        } catch (error) {
            setError(error.message);
        }
    }, [selectedEvent, updateEvent, addEvent, setError, closeEventModal]);

    const handleDeleteEvent = useCallback(async (eventId) => {
        if (!window.confirm("Are you sure you want to delete this event?")) return;
        try {
            setError(null);
            await deleteEvent(eventId);
            closeEventModal();
        } catch (error) {
            setError(error.message);
        }
    }, [deleteEvent, setError, closeEventModal]);

    useEffect(() => {
        const loadColors = async () => {
            try {
                const q = query(
                    collection(db, "users"),
                    where("user_role", "in", ["inspector", "carpenter", "director"])
                );
                const snapshot = await getDocs(q);

                const inspectors = {};
                const carpenters = {};
                const directors = {};

                snapshot.docs.forEach(d => {
                    const { color, user_role } = d.data();
                    if (!color) return;

                    if (user_role === "inspector") inspectors[d.id] = color;
                    else if (user_role === "carpenter") carpenters[d.id] = color;
                    else if (user_role === "director") directors[d.id] = color;
                });

                setInspectorColorMap(inspectors);
                setCarpenterColorMap(carpenters);
                setDirectorColorMap(directors);
            } catch (err) {
                console.error("Error loading user colors:", err);
            }
        };
        loadColors();
    }, []);

    useEffect(() => {
        if (
            Object.keys(inspectorColorMap).length === 0 &&
            Object.keys(carpenterColorMap).length === 0 &&
            Object.keys(directorColorMap).length === 0
        ) return;
        calendarRef.current?.getApi().refetchEvents();
    }, [inspectorColorMap, carpenterColorMap, directorColorMap]);

    const getInspectorColor = (uid) => inspectorColorMap[uid];
    const getCarpenterColor = (uid) => carpenterColorMap[uid];
    const getDirectorColor = (uid) => directorColorMap[uid];


    const eventContent = useCallback((eventInfo) => {
        const lunch = eventInfo.event.extendedProps?.lunchDuration || 0;

        const inspectorColor = getInspectorColor(eventInfo.event.extendedProps?.assignedInspector);
        const carpenterColor = getCarpenterColor(eventInfo.event.extendedProps?.assignedCarpenter);
        const directorColor = getDirectorColor(eventInfo.event.extendedProps?.assignedDirector);
        const eventType = eventInfo.event.extendedProps?.category || "general";
        const eventColor = eventColors[eventType] || eventColors.general;

        const travel = eventInfo.event.extendedProps?.travelTimeMinutes || 0;
        const surveyRaw = eventInfo.event.extendedProps?.surveyTimeMinutes;

        const total = travel + (surveyRaw != null ? surveyRaw : 0);
        const survey = surveyRaw != null ? surveyRaw : total - travel;

        const travelPercent = total > 0 ? (travel / total) * 100 : 0;
        const surveyPercent = total > 0 ? (survey / total) * 100 : 0;

        const viewType = eventInfo.view?.type || 'dayGridMonth';

        const gradientDirection = viewType === 'dayGridMonth' ? 'to bottom' : 'to bottom';

        return {
            html: `
        <div style="
            height: 100%;
            width: 100%;
            ${total > 0 ? `
            background: linear-gradient(
                ${gradientDirection},
                #fc9d04ff ${travelPercent}%,
                ${eventColor.bg} ${travelPercent}% ${travelPercent + surveyPercent}%,
                ${eventColor.bg} ${travelPercent + surveyPercent}%
            );` : `background: ${eventColor.bg};`}
            color: ${eventColor.text};
           border-left: 8px solid ${eventInfo.event.extendedProps?.assignedInspector
                    ? inspectorColor
                    : eventInfo.event.extendedProps?.assignedCarpenter
                        ? carpenterColor
                        : directorColor
                };
            border-radius: 8px;
            padding: 2px;
            font-size: ${isMobile ? '0.75rem' : '0.8rem'};
            position: relative;
            overflow: hidden;
        ">
            <div style="
                font-weight: 600;
                white-space: nowrap;
                overflow: hidden;
                text-overflow: ellipsis;
                text-align: center;
                position: relative;
                z-index: 1;
                padding: 2px;
                border-radius: 3px;
                display: inline-block;
                margin: 0 auto;
                cursor: pointer;
            ">
                ${eventInfo.event.title}
            </div>
        </div>
        `
        };
    }, [isMobile, inspectorColorMap, carpenters, directors]);


    async function scheduleLatestReports(user) {
        if (!user?.uid) {
            console.error("Missing user.uid");
            return;
        }
        setLoadingScheudle(true);

        // console.log("Scheduling started...");

        const reportsRef = collection(db, "Reports");
        const reportsSnap = await getDocs(reportsRef);
        // console.log(`Total reports fetched: ${reportsSnap.size}`);

        const reportsData = await Promise.all(
            reportsSnap.docs.map(async (docSnap) => {
                try {
                    const data = docSnap.data();
                    const { buildingName, email, floors = 0, date: rawDate } = data;
                    if (!buildingName || !rawDate) return null;

                    const parsedDate = dayjs(rawDate, "YYYY-MM-DD HH:mm:ss");
                    if (!parsedDate.isValid()) return null;

                    const [doorSnap, resSnap] = await Promise.allSettled([
                        getDocs(collection(docSnap.ref, "DoorReports")),
                        getDocs(collection(docSnap.ref, "ResidentialReports")),
                    ]);

                    let doorDocs = [];
                    if (doorSnap.status === "fulfilled") {
                        doorDocs = doorSnap.value.docs.filter(
                            (d) => d.data().addedBy && d.data().addedBy.trim() !== ""
                        );
                    }
                    const resDocs = resSnap.status === "fulfilled" ? resSnap.value.docs : [];

                    if (doorDocs.length === 0 && resDocs.length === 0) return null;

                    return {
                        buildingUID: docSnap.id,
                        buildingName,
                        floors,
                        email,
                        parsedDate,
                        doorCount: doorDocs.length,
                        resCount: resDocs.length,
                        originalDoorSnap: doorSnap.status === "fulfilled" ? doorSnap.value.docs : [],
                    };
                } catch (err) {
                    console.warn("Skipped invalid report:", err.message);
                    return null;
                }
            })
        );

        const validReports = reportsData.filter(Boolean);
        // console.log(`Valid reports after door filter: ${validReports.length}`);

        const grouped = {};
        for (const r of validReports) {
            if (!grouped[r.buildingName]) grouped[r.buildingName] = [];
            grouped[r.buildingName].push(r);
        }

        const latestReports = [];

        for (const [bName, reports] of Object.entries(grouped)) {

            reports.sort((a, b) => b.parsedDate.valueOf() - a.parsedDate.valueOf());

            let latestCommunal = null;
            let latestResidential = null;

            for (const r of reports) {
                const { parsedDate, doorCount, resCount, originalDoorSnap } = r;

                let skipCommunal = false;
                if (doorCount > 0) {
                    const invalidDoor = originalDoorSnap.some(
                        (d) => !d.data().addedBy || d.data().addedBy.trim() === ""
                    );
                    if (invalidDoor) skipCommunal = true;
                }

                if (!skipCommunal && doorCount > 0) {
                    if (!latestCommunal) {
                        latestCommunal = { ...r, type: "communal" };
                    }
                }

                if (resCount > 0) {
                    if (!latestResidential) {
                        latestResidential = { ...r, type: "residential" };
                    }
                }

                if (latestCommunal && latestResidential) break;
            }

            if (!latestCommunal) {
                const fallback = reports.find((r) => r.doorCount > 0);
                if (fallback) latestCommunal = { ...fallback, type: "communal" };
            }

            if (latestCommunal) latestReports.push(latestCommunal);
            if (latestResidential) latestReports.push(latestResidential);
        }

        // console.log(`Latest reports (communal + residential): ${latestReports.length}`);

        const userCalendarRef = calendarCollection();
        const existingEventsSnap = await getDocs(userCalendarRef);
        const existingEvents = existingEventsSnap.docs.map((d) => d.data());
        // console.log(`Existing events found: ${existingEvents.length}`);

        for (const r of latestReports) {
            const { buildingName, email, floors, parsedDate, type, buildingUID } = r;

            const scheduledDates = [];
            if (type === "communal" && floors > 5) {
                for (let i = 3; i <= 12; i += 3) scheduledDates.push(parsedDate.add(i, "month"));
            } else {
                scheduledDates.push(parsedDate.add(12, "month"));
            }

            for (const scheduledDate of scheduledDates) {
                const start = scheduledDate.format("DD/MM/YYYY HH:mm:ss");
                const end = scheduledDate.add(1, "hour").format("DD/MM/YYYY HH:mm:ss");

                const alreadyExists = existingEvents.some(
                    (e) =>
                        e.buildingUID === buildingUID &&
                        e.category === type &&
                        e.start === start
                );

                if (alreadyExists) {
                    // console.log(`Skipped duplicate for ${buildingName} (${type}) → ${start}`);
                    continue;
                }

                const eventData = {
                    title: buildingName,
                    description: "",
                    type: "type",
                    location: buildingName,
                    start,
                    end,
                    buildingUID,
                    category: type,
                    buildingEmail: email,
                };

                try {
                    await addDoc(userCalendarRef, eventData);
                    // console.log(`Scheduled ${buildingName} (${type}) → ${start}`);
                } catch (err) {
                    console.error(`Failed to schedule ${buildingName}:`, err.message);
                }
            }
        }

        // console.log("Scheduling original reports...");

        await Promise.all(
            reportsSnap.docs.map(async (docSnap) => {
                const data = docSnap.data();
                const { buildingName, email, date: rawDate } = data;
                if (!buildingName || !rawDate) return;

                const parsedDate = dayjs(rawDate, "YYYY-MM-DD HH:mm:ss");
                if (!parsedDate.isValid()) return;

                const buildingUID = docSnap.id;

                const [doorSnap, resSnap] = await Promise.all([
                    getDocs(collection(docSnap.ref, "DoorReports")),
                    getDocs(collection(docSnap.ref, "ResidentialReports")),
                ]);

                let types = [];
                if (doorSnap.docs.length > 0) types.push("communal");
                if (resSnap.docs.length > 0) types.push("residential");
                if (types.length === 0) types.push("general");

                await Promise.all(
                    types.map(async (type) => {
                        const start = parsedDate.format("DD/MM/YYYY HH:mm:ss");
                        const end = parsedDate.add(1, "hour").format("DD/MM/YYYY HH:mm:ss");

                        const alreadyExists = existingEvents.some(
                            (e) => e.buildingUID === buildingUID && e.category === type && e.start === start
                        );
                        if (alreadyExists) return;

                        const eventData = {
                            title: buildingName,
                            description: "",
                            type,
                            location: buildingName,
                            start,
                            end,
                            buildingUID,
                            category: type,
                            buildingEmail: email,
                        };

                        try {
                            await addDoc(userCalendarRef, eventData);
                            // console.log(`Scheduled ORIGINAL report ${buildingName} (${type}) → ${start}`);
                        } catch (err) {
                            console.error(`Failed to schedule ${buildingName}:`, err.message);
                        }
                    })
                );
            })
        );

        // console.log("Scheduling complete — duplicates prevented.");
        setLoadingScheudle(false);
    }


    const handleNavigate = useCallback((action) => {
        if (calendarRef.current) {
            const calendarApi = calendarRef.current.getApi();
            if (action === "TODAY") {
                calendarApi.today();
                setCurrentDate(new Date());
            } else if (action === "PREV") {
                calendarApi.prev();
                setCurrentDate(calendarApi.getDate());
            } else if (action === "NEXT") {
                calendarApi.next();
                setCurrentDate(calendarApi.getDate());
            }
        }
    }, []);

    const handleViewChange = useCallback((newView) => {
        setView(newView);
        if (calendarRef.current) {
            calendarRef.current.getApi().changeView(newView);
        }
    }, []);

    const CustomToolbar = useCallback(() => {
        const getTitle = () => {
            if (calendarRef.current) {
                const calendarApi = calendarRef.current.getApi();
                const currentView = calendarApi.view;
                switch (currentView.type) {
                    case 'dayGridMonth':
                        return format(currentView.currentStart, 'MMMM yyyy');
                    case 'timeGridWeek':
                    case 'timeGridDay':
                    case 'listWeek':
                        return format(currentView.currentStart, 'MMM d, yyyy');
                    default:
                        return format(new Date(), 'MMMM yyyy');
                }
            }
            return format(new Date(), 'MMMM yyyy');
        };

        return (
            <div className="calendar-toolbar">
                <div className="toolbar-row-1">
                    <div className="toolbar-navigation">
                        <button className="btn btn-sm btn-outline-primary btn-today" onClick={() => handleNavigate("TODAY")}>
                            Today
                        </button>
                        <div className="btn-nav-group">
                            <button className="btn btn-sm btn-outline-secondary" onClick={() => handleNavigate("PREV")}>
                                ←
                            </button>
                            <button className="btn btn-sm btn-outline-secondary" onClick={() => handleNavigate("NEXT")}>
                                →
                            </button>
                        </div>
                    </div>
                    <h2 className="toolbar-title">
                        {getTitle()}
                    </h2>
                </div>
                <div className="toolbar-row-2">
                    <div className="btn-group btn-group-views">
                        {!isMobile && (
                            <button
                                className={`btn btn-sm ${view === 'dayGridMonth' ? "btn-primary" : "btn-outline-secondary"}`}
                                onClick={() => handleViewChange('dayGridMonth')}
                            >
                                Month
                            </button>
                        )}
                        <button
                            className={`btn btn-sm ${view === 'timeGridWeek' ? "btn-primary" : "btn-outline-secondary"}`}
                            onClick={() => handleViewChange('timeGridWeek')}
                        >
                            Week
                        </button>
                        <button
                            className={`btn btn-sm ${view === 'timeGridDay' ? "btn-primary" : "btn-outline-secondary"}`}
                            onClick={() => handleViewChange('timeGridDay')}
                        >
                            Day
                        </button>
                        <button
                            className={`btn btn-sm ${view === 'listWeek' ? "btn-primary" : "btn-outline-secondary"}`}
                            onClick={() => handleViewChange('listWeek')}
                        >
                            Agenda
                        </button>
                    </div>
                    {(userData.role === 'admin' || userData.role === 'manager' || userData.role === 'inspection' || userData.role === 'coordinator' || userData.role === 'remedial') && (
                        <button
                            className="btn btn-sm btn-primary btn-create"
                            onClick={() => openCreateEventModal()}
                        >
                            <span>+</span> <span className="btn-create-text">Create</span>
                        </button>
                    )}
                </div>
            </div>
        );
    }, [view, isMobile, handleNavigate, handleViewChange, userData, openCreateEventModal]);

    function formatTimeOnly(dateInput) {
        if (dateInput instanceof Date) {
            let hour = dateInput.getHours();
            const minuteStr = String(dateInput.getMinutes()).padStart(2, "0");

            const ampm = hour >= 12 ? "PM" : "AM";
            if (hour === 0) hour = 12;
            else if (hour > 12) hour -= 12;

            return `${String(hour).padStart(2, "0")}:${minuteStr} ${ampm}`;
        }

        const parts = dateInput.split(" ");
        if (parts.length < 2) return "";

        const timePart = parts[1];
        const [hourStr, minuteStr] = timePart.split(":");

        let hour = parseInt(hourStr, 10);
        const ampm = hour >= 12 ? "PM" : "AM";

        if (hour === 0) hour = 12;
        else if (hour > 12) hour -= 12;

        return `${String(hour).padStart(2, "0")}:${minuteStr} ${ampm}`;
    }

    const isAutoTitle = (title) => /^\d{1,2}:\d{2}\s?(AM|PM)\s?•/.test(title);

    const calendarEvents = useMemo(() => {
        return events.map(event => ({
            id: event.id,
            title: (() => {
                if (!event.title || isAutoTitle(event.title)) {
                    const startTimeStr = formatTimeOnly(event.start);
                    const titleTime = event.title?.split(' • ')[0];

                    if (!event.title || titleTime !== startTimeStr) {
                        const address = event.title?.split(' • ').slice(1).join(' • ') || event.buildingAddress;
                        return `${startTimeStr} • ${address}`;
                    }
                }
                return event.title;
            })(),
            start: event.start,
            end: event.end,
            extendedProps: {
                description: event.description,
                type: event.type,
                category: event.category,
                buildingEmail: event.buildingEmail,

                buildingAddress: event.buildingAddress || '',
                buildingCity: event.buildingCity || '',
                travelDistanceMiles: event.travelDistanceMiles || 0,
                travelTimeMinutes: event.travelTimeMinutes || 0,
                surveyTimeMinutes: event.surveyTimeMinutes || 0,
                totalCommunalDoors: event.totalCommunalDoors || 0,
                totalResiDoors: event.totalResiDoors || 0,
                preventRescheduling: event.preventRescheduling || false,
                inspectorName: event.inspectorName || '',
                directorName: event.directorName || '',
                carpenterName: event.carpenterName || '',
                lunchDuration: event.lunchDuration || '',
                postcode: event.postcode || '',
                assignedInspector: event.assignedInspector || '',
                assignedCarpenter: event.assignedCarpenter || '',
                assignedDirector: event.assignedDirector || '',
                doorUrl: event.doorUrl || '',
                materialCost: event.materialCost || '',
                jobPrice: event.jobPrice || '',
                labourCost: event.labourCost || '',
                ticket_id: event.ticket_id || null,

            }
        })).filter(event => {
            if (view === 'dayGridMonth') {
                const monthStart = startOfMonth(currentDate);
                const monthEnd = endOfMonth(currentDate);
                return isWithinInterval(event.start, { start: monthStart, end: monthEnd }) ||
                    isWithinInterval(event.end, { start: monthStart, end: monthEnd });
            }
            return true;
        });
    }, [events, view, currentDate]);

    if (loadingAuth) {
        return (
            <div className="d-flex justify-content-center align-items-center vh-100">
                <div className="spinner-border text-primary" role="status"></div>
            </div>
        );
    }

    if (!user) {
        return <div className="text-center p-5">Please sign in to access your calendar.</div>;
    }

    function toTitleCase(str) {
        return str
            .toLowerCase()
            .split(" ")
            .map(word => word.charAt(0).toUpperCase() + word.slice(1))
            .join(" ");
    }

    async function exportSchedule() {
        setShowExportModal(true);
        setInspectores([]);
        setCarpenters([]);
        setDirectors([]);
        setSelectedInspector("");
        setSelectedDirector("");
        setSelectedCarpenter("");

        try {
            setExportLoading(true);
            const q = query(collection(db, "users"), where("user_role", "==", "inspector"));
            const snapshot = await getDocs(q);
            const list = snapshot.docs
                .map(doc => {
                    const name = doc.data().name;
                    return { value: doc.id, label: name ? toTitleCase(name) : "" };
                })
                .filter(u => u.label);
            setInspectores(list);
        } catch (e) {
            console.error("Failed to load inspectors", e);
        } finally {
            setExportLoading(false);
        }

        try {
            setExportLoading(true);
            const q = query(collection(db, "users"), where("user_role", "==", "carpenter"));
            const snapshot = await getDocs(q);
            const list = snapshot.docs
                .map(doc => {
                    const name = doc.data().name;
                    return { value: doc.id, label: name ? toTitleCase(name) : "" };
                })
                .filter(u => u.label);
            setCarpenters(list);
        } catch (e) {
            console.error("Failed to load carpenter", e);
        } finally {
            setExportLoading(false);
        }

        try {
            setExportLoading(true);
            const q = query(collection(db, "users"), where("user_role", "==", "director"));
            const snapshot = await getDocs(q);
            const list = snapshot.docs
                .map(doc => {
                    const name = doc.data().name;
                    return { value: doc.id, label: name ? toTitleCase(name) : "" };
                })
                .filter(u => u.label);
            setDirectors(list);
        } catch (e) {
            console.error("Failed to load director", e);
        } finally {
            setExportLoading(false);
        }
    }

    function parseDateTime(dateInput) {
        if (!dateInput) return null;

        if (dateInput.toDate && typeof dateInput.toDate === "function") return dateInput.toDate();

        if (dateInput instanceof Date) return dateInput;

        if (typeof dateInput === "string") {
            if (/\d{1,2}\/\d{1,2}\/\d{4}/.test(dateInput)) {
                const [day, month, rest] = dateInput.split("/");
                const [year, time = "00:00:00"] = rest.split(" ");
                return new Date(`${year}-${month.padStart(2, "0")}-${day.padStart(2, "0")}T${time}`);
            }
            const d = new Date(dateInput);
            if (!isNaN(d)) return d;
        }
        const d = new Date(dateInput);
        return isNaN(d) ? null : d;
    }

    function formatTimeRange(startInput, endInput) {
        const s = parseDateTime(startInput);
        const e = parseDateTime(endInput);
        if (!s || !e) return "";

        const formatAMPM = (date) => {
            let hours = date.getHours();
            const minutes = date.getMinutes();
            const ampm = hours >= 12 ? "PM" : "AM";
            hours = hours % 12;
            hours = hours ? hours : 12;
            return `${hours.toString().padStart(2, "0")}:${minutes.toString().padStart(2, "0")} ${ampm}`;
        };

        return `${formatAMPM(s)} – ${formatAMPM(e)}`;
    }

    function formatWeekdayAndDate(startInput) {
        const d = parseDateTime(startInput);
        if (!d) return "";
        const weekday = d.toLocaleDateString("en-GB", { weekday: "long" });
        const dd = d.getDate().toString().padStart(2, "0");
        const mm = (d.getMonth() + 1).toString().padStart(2, "0");
        return `${weekday} ${dd}/${mm}`;
    }

    function makeAddress(ev) {
        return ev.buildingAddress || ""
    }

    function companyFromEvent(ev) {
        return ev.buildingEmail || "-";
    }

    function addMinutes(date, minutes) {
        return new Date(date.getTime() + minutes * 60000);
    }

    function formatTime(date) {
        return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    }

    function formatRange(start, end) {
        return `${formatTime(start)} – ${formatTime(end)}`;
    }

    function extractBuildingAndPostcode(address) {
        const parts = address.split(',').map(p => p.trim());

        const buildingName = parts.slice(0, -2).join(', ');
        const postcode = parts.length >= 2 ? parts[parts.length - 2] : '';

        return { buildingName, postcode };
    }


    async function fetchPostcodeForBuilding(buildingAddress) {
        if (!buildingAddress) return '';

        const STOP_WORDS = new Set([
            'the', 'a', 'an', 'and', 'or', 'of', 'at', 'in', 'on',
            'st', 'rd', 'ave', 'ln', 'ct', 'pl', 'sq', 'dr',
            'street', 'road', 'avenue', 'lane', 'court', 'place', 'square', 'drive',
            'north', 'south', 'east', 'west'
        ]);

        const normalize = (str) => str
            ?.toLowerCase()
            .replace(/\bstreet\b/g, 'st')
            .replace(/\broad\b/g, 'rd')
            .replace(/\bavenue\b/g, 'ave')
            .replace(/\bdrive\b/g, 'dr')
            .replace(/\blane\b/g, 'ln')
            .replace(/\bcourt\b/g, 'ct')
            .replace(/\bplace\b/g, 'pl')
            .replace(/\bsquare\b/g, 'sq')
            .replace(/[.,]+/g, '')
            .replace(/\s+/g, ' ')
            .trim() || '';

        const meaningfulTokens = (str) =>
            normalize(str)
                .split(' ')
                .filter(t => t.length >= 3 && !STOP_WORDS.has(t) && !/^\d+$/.test(t));

        const similarity = (a, b) => {
            const tokensA = meaningfulTokens(a);
            const tokensB = meaningfulTokens(b);
            if (!tokensA.length || !tokensB.length) return 0;

            const matches = tokensA.filter(t => tokensB.includes(t)).length;

            const scoreAB = matches / tokensA.length;
            const scoreBA = matches / tokensB.length;
            return Math.max(scoreAB, scoreBA);
        };

        const inputName = buildingAddress.split(',')[0].trim();
        const inputTokens = meaningfulTokens(inputName);

        if (!inputTokens.length) return '';

        try {
            const reportsRef = collection(db, 'Reports');

            const exactQ = query(reportsRef, where('buildingName', '==', inputName), limit(1));
            const exactSnap = await getDocs(exactQ);
            if (!exactSnap.empty) return exactSnap.docs[0].data().postcode || '';

            const firstToken = inputTokens[0];
            const allReports = await getDocs(reportsRef);

            let bestMatch = null;
            let bestScore = 0;
            const THRESHOLD = 0.75;

            for (const doc of allReports.docs) {
                const reportName = doc.data().buildingName || '';
                const reportTokens = meaningfulTokens(reportName);

                if (!reportTokens.includes(firstToken)) continue;

                const score = similarity(inputName, reportName);
                if (score > bestScore) {
                    bestScore = score;
                    bestMatch = doc;
                }
            }

            if (bestMatch && bestScore >= THRESHOLD) {
                // console.log(`Matched "${inputName}" → "${bestMatch.data().buildingName}" (${Math.round(bestScore * 100)}%)`);
                return bestMatch.data().postcode || '';
            }

        } catch (error) {
            console.error('Error fetching postcode:', error);
        }

        return '';
    }

    function generateQRDataURL(url, size = 28) {
        return new Promise((resolve, reject) => {
            const canvas = document.createElement("canvas");
            QRCode.toCanvas(canvas, url, { width: size }, (err) => {
                if (err) reject(err);
                else resolve(canvas.toDataURL("image/png"));
            });
        });
    }

    const formatLunchTime = (duration) => {
        if (!duration) return '0m';

        const mins = parseInt(duration);
        const endHour = 12 + Math.floor(mins / 60);
        const displayHour = endHour > 12 ? endHour - 12 : endHour;
        return `12:00PM - ${String(displayHour).padStart(2, '0')}:00PM`;
    };

    async function handleRunExport(startDate, endDate) {
        if (!selectedInspector && !selectedCarpenter && !selectedDirector) {
            console.warn("No inspector or carpenter selected");
            return;
        }

        const showDoorColumns = !selectedInspector;

        const start = startDate ? new Date(new Date(startDate).setHours(0, 0, 0, 0)) : null;
        const end = endDate ? new Date(new Date(endDate).setHours(23, 59, 59, 999)) : null;

        const filtered = (events || []).filter(ev => {
            if (selectedInspector && ev.assignedInspector !== selectedInspector) return false;
            if (selectedCarpenter && ev.assignedCarpenter !== selectedCarpenter) return false;
            if (selectedDirector && ev.assignedDirector !== selectedDirector) return false;

            const evStart = parseDateTime(ev.start);
            if (!evStart) return false;
            if (start && evStart < start) return false;
            if (end && evStart > end) return false;
            return true;
        });

        if (!filtered.length) {
            console.info("No events in the selected range.");
            return;
        }

        filtered.sort((a, b) => {
            const da = parseDateTime(a.start);
            const db = parseDateTime(b.start);
            return (da && db) ? da - db : 0;
        });

        const grouped = {};
        filtered.forEach(ev => {
            const dayLabel = formatWeekdayAndDate(ev.start);
            if (!grouped[dayLabel]) grouped[dayLabel] = [];
            grouped[dayLabel].push(ev);
        });

        const inspectorName = selectedInspector
            ? (inspectores || []).find(i => i.value === selectedInspector)?.label || selectedInspector
            : selectedCarpenter
                ? (carpenters || []).find(i => i.value === selectedCarpenter)?.label || selectedCarpenter
                : (directors || []).find(i => i.value === selectedDirector)?.label || selectedDirector || '';

        const doc = new jsPDF({
            unit: "mm",
            format: "a4",
            orientation: "landscape",
            compress: true
        });

        const pageWidth = doc.internal.pageSize.getWidth();
        const pageHeight = doc.internal.pageSize.getHeight();
        const margin = 7;
        let cursorY = 18;

        doc.setFont("helvetica", "bold");
        doc.setFontSize(18);
        doc.text(`Schedule for ${inspectorName}`, margin, cursorY);

        const eventDates = filtered.flatMap(ev => [parseDateTime(ev.start), parseDateTime(ev.end)]);
        const validDates = eventDates.filter(d => d instanceof Date && !isNaN(d));
        const minDate = start || new Date(Math.min(...validDates));
        const maxDate = end || new Date(Math.max(...validDates));

        const options = { day: "2-digit", month: "short", year: "numeric" };
        const periodTextValue = `${minDate.toLocaleDateString("en-GB", options)} – ${maxDate.toLocaleDateString("en-GB", options)}`;

        doc.setFont("helvetica", "normal");
        doc.setFontSize(9);
        const generatedText = `Generated: ${new Date().toLocaleDateString("en-GB")}`;
        const periodText = `Period: ${periodTextValue}`;
        const generatedX = pageWidth - margin - doc.getTextWidth(generatedText);
        const periodX = pageWidth - margin - doc.getTextWidth(periodText);
        doc.text(generatedText, generatedX, cursorY);
        doc.text(periodText, periodX, cursorY + 4);

        cursorY += 10;

        const dayKeys = Object.keys(grouped);

        for (let di = 0; di < dayKeys.length; di++) {
            const dayLabel = dayKeys[di];
            const dayEvents = grouped[dayLabel];

            const qrMap = {};
            if (showDoorColumns) {
                await Promise.all(dayEvents.map(async (ev, idx) => {
                    if (ev.doorUrl) {
                        try {
                            qrMap[idx] = await generateQRDataURL(ev.doorUrl, 28);
                        } catch (e) {
                            console.warn("QR generation failed for", ev.doorUrl, e);
                        }
                    }
                }));
            }

            const isTrevor = inspectorName.replace(/[^a-z0-9_\-]/gi, "_").toLowerCase() === 'trevorm';

            const rows = await Promise.all(dayEvents.map(async (ev, idx) => {
                const evStart = new Date(ev.start);
                const evEnd = new Date(ev.end);
                const travelMinutes = Number(ev.travelTimeMinutes) || 0;
                const inspectionStart = addMinutes(evStart, travelMinutes);
                const travelTime = travelMinutes > 0 ? formatRange(evStart, inspectionStart) : '';
                const inspectionTime = formatRange(inspectionStart, evEnd);
                const address = makeAddress(ev);
                const postcode = ev.postcode || await fetchPostcodeForBuilding(address);
                const resiDoors = Number(ev.totalResiDoors) || 0;
                const communalDoors = Number(ev.totalCommunalDoors) || 0;
                const client = companyFromEvent(ev);
                const description = ev.description || ev.notes || '—';
                const doorUrl = ev.doorUrl || '';

                return [
                    travelTime,
                    inspectionTime,
                    address,
                    postcode,
                    resiDoors,
                    communalDoors,
                    client,
                    isTrevor
                        ? formatLunchTime(ev.lunchDuration)
                        : (ev.lunchDuration ? '60m' : '0m'),
                    ...(showDoorColumns ? [{ url: ev.doorUrl || '', rowIndex: idx }] : []),
                    description,
                ];
            }));

            const estimatedTableHeight = 8 + dayEvents.reduce((sum, ev) => {
                return sum + (showDoorColumns && ev.doorUrl ? 18 : 8);
            }, 0) + 6;

            if (cursorY + estimatedTableHeight > pageHeight - 10) {
                doc.addPage();
                cursorY = 18;
            }

            doc.setFontSize(14);
            doc.setFont(undefined, "bold");
            doc.text(dayLabel, margin, cursorY);
            cursorY += 6;
            doc.setFont(undefined, "normal");

            const baseWidths = {
                0: { cellWidth: 36 },
                1: { cellWidth: 36 },
                2: { cellWidth: 30 },
                3: { cellWidth: 18, halign: "center" },
                4: { cellWidth: 18, halign: "center" },
                5: { cellWidth: 18, halign: "center" },
                6: { cellWidth: 30 },
                7: { cellWidth: 22, halign: "center" },
            };

            let columnStyles = { ...baseWidths };
            let nextIndex = Object.keys(baseWidths).length;

            if (showDoorColumns) {
                columnStyles[nextIndex++] = { cellWidth: 30 };
            }

            columnStyles[nextIndex] = {
                cellWidth: pageWidth - margin * 2 - Object.values(columnStyles).reduce((sum, col) => sum + (col.cellWidth || 0), 0),
                fontStyle: "italic",
                textColor: 80,
            };

            autoTable(doc, {
                startY: cursorY,
                head: [[
                    "Travel Time",
                    "Inspection Time",
                    "Building Address",
                    "Postcode",
                    "R. Doors",
                    "C. Doors",
                    "Client",
                    "Lunch Break",
                    ...(showDoorColumns ? ["Door"] : []),
                    "Description",
                ]],
                body: rows,
                theme: "grid",
                styles: {
                    fontSize: 8,
                    cellPadding: 2,
                    overflow: "linebreak",
                    valign: "middle",
                },
                headStyles: {
                    fillColor: [240, 240, 240],
                    textColor: 20,
                    fontStyle: "bold",
                    fontSize: 8,
                    halign: "center",
                    valign: "middle",
                },
                columnStyles,
                margin: { left: margin, right: margin },
                didParseCell: function (data) {
                    if (data.section === "body" && showDoorColumns && data.column.index === 8) {
                        data.cell.text = [""];
                        const raw = data.cell.raw;
                        if (raw && raw.url) {
                            data.cell.styles.minCellHeight = 18;
                        }
                    }
                },
                didDrawCell: function (data) {
                    if (data.section !== "body") return;
                    if (!showDoorColumns || data.column.index !== 8) return;

                    const raw = data.cell.raw;
                    if (!raw || !raw.url) return;

                    const url = raw.url;
                    const rowIndex = raw.rowIndex;

                    const qrDataUrl = qrMap[rowIndex];
                    if (qrDataUrl) {
                        const qrSize = 16;
                        const qrX = data.cell.x + (data.cell.width - qrSize) / 2;
                        const qrY = data.cell.y + 1;

                        doc.addImage(qrDataUrl, "PNG", qrX, qrY, qrSize, qrSize);

                        doc.link(qrX, qrY, qrSize, qrSize, { url });
                    }
                },
            });

            cursorY = (doc.lastAutoTable ? doc.lastAutoTable.finalY : cursorY + 6) + 12;

            if (cursorY > pageHeight - 30 && di < dayKeys.length - 1) {
                doc.addPage();
                cursorY = 18;
            }
        }

        const pageCount = doc.getNumberOfPages();
        for (let i = 1; i <= pageCount; i++) {
            doc.setPage(i);
            const footerText = `Page ${i} of ${pageCount}`;
            doc.setFontSize(7);
            doc.setTextColor(150);
            const pw = doc.internal.pageSize.getWidth();
            doc.text(footerText, pw - 14, doc.internal.pageSize.getHeight() - 8, { align: "right" });
            doc.setTextColor(0);
        }

        const safeName = inspectorName.replace(/[^a-z0-9_\-]/gi, "_").toLowerCase();
        const optionsFile = { day: "2-digit", month: "short", year: "numeric" };
        const startStr = minDate.toLocaleDateString("en-GB", optionsFile).replace(/ /g, "-");
        const endStr = maxDate.toLocaleDateString("en-GB", optionsFile).replace(/ /g, "-");

        doc.save(`schedule-${safeName}-${startStr}_to_${endStr}.pdf`);

        setShowExportModal(false);
    }

    const filteredQueryEvents = calendarEvents.filter((event) => {
        const title = event.title?.toLowerCase() || '';
        const building = event.extendedProps?.buildingAddress?.toLowerCase() || '';
        const city = event.extendedProps?.buildingCity?.toLowerCase() || '';
        const inspector = event.extendedProps?.inspectorName?.toLowerCase() || '';
        const director = event.extendedProps?.directorName?.toLowerCase() || '';
        const carpenter = event.extendedProps?.carpenterName?.toLowerCase() || '';
        const postcode = event.extendedProps?.postcode?.toLowerCase() || '';

        let fieldMatch = true;
        if (searchField === 'inspector') fieldMatch = inspector !== '';
        if (searchField === 'carpenter') fieldMatch = carpenter !== '';
        if (searchField === 'director') fieldMatch = director !== '';

        if (!searchQuery) return fieldMatch;

        const query = searchQuery.toLowerCase();

        const textMatch =
            title.includes(query) ||
            building.includes(query) ||
            city.includes(query) ||
            inspector.includes(query) ||
            director.includes(query) ||
            carpenter.includes(query) ||
            postcode.includes(query);

        return fieldMatch && textMatch;
    });

    const getSearchOptions = () => {
        const { role } = userData;

        const options = {
            admin: ['all', 'inspector', 'carpenter', 'director'],
            manager: ['all', 'inspector', 'carpenter', 'director'],
            inspection: ['all', 'inspector', 'carpenter', 'director'],
            coordinator: ['all', 'inspector', 'carpenter', 'director'],
            remedial: ['all', 'inspector', 'carpenter', 'director'],
            reviewer: ['all', 'inspector', 'carpenter'],
            client: ['all', 'inspector', 'carpenter'],
            inspector: ['inspector'],
            carpenter: ['carpenter'],
        };

        return options[role] || ['all'];
    };

    const searchOptions = getSearchOptions();
    const labels = {
        all: 'All',
        inspector: 'Inspector',
        carpenter: 'Carpenter',
        director: 'Director',
    };

    return (
        <div className="calendar-page">
            <div
                className="calendar-header mb-4"
                style={{
                    width: "calc(100% - 32px)",
                    margin: "16px",
                    backgroundColor: "#fff",
                    border: "1px solid #e9ecef",
                    borderRadius: "12px",
                    padding: "16px",
                    boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
                    boxSizing: "border-box",
                }}
            >
                <div className="d-none d-lg-flex align-items-center gap-3 w-100">

                    <h1
                        className="mb-0"
                        style={{
                            fontSize: "1.5rem",
                            fontWeight: 700,
                            whiteSpace: "nowrap",
                        }}
                    >
                        FireChecked Calendar
                    </h1>

                    <div className="d-flex flex-grow-1 justify-content-center">
                        <div
                            className="d-flex gap-2 w-100"
                            style={{ maxWidth: "820px" }}
                        >
                            <select
                                className="form-select"
                                value={searchField}
                                onChange={(e) => {
                                    setSearchField(e.target.value);
                                    setSearchQuery("");
                                }}
                                style={{ width: "190px" }}
                            >
                                {searchOptions.map((opt) => (
                                    <option key={opt} value={opt}>
                                        {labels[opt]}
                                    </option>
                                ))}
                            </select>

                            <input
                                className="form-control"
                                value={searchQuery}
                                onChange={(e) =>
                                    setSearchQuery(e.target.value)
                                }
                                placeholder={
                                    searchField === 'inspector' ? 'Search inspectors by title, address or city...' :
                                        searchField === 'carpenter' ? 'Search carpenters by title, address or city...' :
                                            searchField === 'director' ? 'Search directors by title, address or city...' :
                                                'Search by title, address, city or assignee...'
                                }
                            />

                            <button
                                className="btn btn-outline-secondary"
                                onClick={() => {
                                    setSearchQuery("");
                                    setSearchField(
                                        searchOptions.includes("all")
                                            ? "all"
                                            : searchOptions[0]
                                    );
                                }}
                            >
                                Clear
                            </button>
                        </div>
                    </div>

                    {(userData.role === "admin" ||
                        userData.role === "manager" ||
                        userData.role === "inspection" ||
                        userData.role === "coordinator" ||
                        userData.role === "reviewer" ||
                        userData.role === "remedial") && (
                            <button
                                className="btn btn-primary"
                                onClick={exportSchedule}
                                style={{ whiteSpace: "nowrap" }}
                            >
                                Export Schedule
                            </button>
                        )}
                </div>

                <div className="d-flex d-lg-none flex-column gap-3 w-100">

                    <h1
                        className="mb-0"
                        style={{
                            fontSize: "1.35rem",
                            fontWeight: 700,
                        }}
                    >
                        FireChecked Calendar
                    </h1>

                    <div className="d-flex gap-2 w-100">
                        <select
                            className="form-select"
                            value={searchField}
                            onChange={(e) => {
                                setSearchField(e.target.value);
                                setSearchQuery("");
                            }}
                            style={{ width: "75%" }}
                        >
                            {searchOptions.map((opt) => (
                                <option key={opt} value={opt}>
                                    {labels[opt]}
                                </option>
                            ))}
                        </select>

                        {(userData.role === "admin" ||
                            userData.role === "manager" ||
                            userData.role === "inspection" ||
                            userData.role === "coordinator" ||
                            userData.role === "reviewer" ||
                            userData.role === "remedial") && (
                                <button
                                    className="btn btn-primary"
                                    onClick={exportSchedule}
                                    style={{ width: "25%" }}
                                >
                                    Export
                                </button>
                            )}
                    </div>

                    <div className="d-flex gap-2 w-100">
                        <input
                            className="form-control"
                            value={searchQuery}
                            onChange={(e) =>
                                setSearchQuery(e.target.value)
                            }
                            placeholder={
                                searchField === 'inspector' ? 'Search inspectors by title, address or city...' :
                                    searchField === 'carpenter' ? 'Search carpenters by title, address or city...' :
                                        searchField === 'director' ? 'Search directors by title, address or city...' :
                                            'Search by title, address, city or assignee...'
                            }
                            style={{ width: "75%" }}
                        />

                        <button
                            className="btn btn-outline-secondary"
                            onClick={() => {
                                setSearchQuery("");
                                setSearchField(
                                    searchOptions.includes("all")
                                        ? "all"
                                        : searchOptions[0]
                                );
                            }}
                            style={{ width: "25%" }}
                        >
                            Clear
                        </button>
                    </div>
                </div>
            </div>



            {error && (
                <div className="alert alert-danger alert-dismissible fade show calendar-alert" role="alert">
                    {error}
                    <button type="button" className="btn-close" onClick={() => setError(null)} aria-label="Close"></button>
                </div>
            )}
            <div className="calendar-container">
                {loading ? (
                    <div className="text-center p-5">
                        <div className="spinner-border text-primary" role="status"></div>
                    </div>
                ) : (
                    <>
                        <CustomToolbar />
                        <FullCalendar
                            ref={calendarRef}
                            plugins={[
                                dayGridPlugin,
                                timeGridPlugin,
                                listPlugin,
                                interactionPlugin
                            ]}
                            headerToolbar={true}
                            initialView={view}
                            initialDate={currentDate}
                            events={filteredQueryEvents}
                            selectMirror={true}
                            dayMaxEvents={11}
                            weekends={true}
                            eventContent={eventContent}

                            key={isAdmin ? "admin" : "readonly"}

                            editable={isAdmin}
                            droppable={isAdmin}
                            selectable={isAdmin}

                            select={isAdmin ? handleDateSelect : undefined}

                            eventClick={handleEventClick}

                            eventAllow={() => isAdmin}

                            eventDrop={isAdmin ? handleEventChange : undefined}
                            eventResize={isAdmin ? handleEventChange : undefined}

                            height="100%"
                            contentHeight="auto"

                            slotMinTime="08:00:00"
                            slotMaxTime="20:00:00"
                            slotMinHeight={30}
                            slotDuration="00:30:00"

                            eventOverlap={true}
                            slotEventOverlap={true}
                            dayMaxEventRows={1}
                            expandRows={true}
                            allDaySlot={true}
                            nowIndicator={true}
                            scrollTime="08:00:00"

                            eventResizableFromStart={isAdmin}
                            eventStartEditable={isAdmin}
                            eventDurationEditable={isAdmin}

                            eventTimeFormat={{
                                hour: "2-digit",
                                minute: "2-digit",
                                hour12: true,
                            }}

                            views={{
                                timeGridWeek: {
                                    slotDuration: "00:30:00",
                                    slotMinHeight: 30,
                                },

                                timeGridDay: {
                                    slotDuration: "00:30:00",
                                    slotMinHeight: 30,
                                },

                                dayGridMonth: {
                                    fixedWeekCount: false,
                                },
                            }}

                            className="professional-calendar"
                        />


                    </>
                )}
            </div>

            {showEventModal && (
                <EventModal
                    user={user}
                    event={selectedEvent}
                    slot={selectedSlot}
                    onCopy={copyEvent}
                    onSave={handleSaveEvent}
                    onDelete={selectedEvent?.id ? () => handleDeleteEvent(selectedEvent.id) : null}
                    onClose={closeEventModal}
                    isMobile={isMobile}
                />
            )}

            {userData.role === "client" && (
                <UserEventModal
                    show={showUserEventModal}
                    event={selectedUserEvent}
                    onClose={() => {
                        setShowUserEventModal(false);
                        setSelectedUserEvent(null);
                    }}
                />
            )}

            {showExportModal && (
                <div
                    style={{
                        position: "fixed",
                        top: 0,
                        left: 0,
                        width: "100vw",
                        height: "100vh",
                        backgroundColor: "rgba(0,0,0,0.5)",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        zIndex: 1050
                    }}
                >
                    <div
                        style={{
                            backgroundColor: "#fff",
                            borderRadius: "8px",
                            padding: "20px",
                            width: "400px",
                            maxWidth: "90%",
                            boxShadow: "0 5px 15px rgba(0,0,0,0.3)",
                            position: "relative"
                        }}
                    >
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "15px" }}>
                            <h5 className="modal-title m-0">Export Schedule</h5>
                            <button
                                type="button"
                                onClick={() => setShowExportModal(false)}
                                style={{
                                    border: "none",
                                    background: "transparent",
                                    fontSize: "20px",
                                    cursor: exportLoading ? "not-allowed" : "pointer",
                                    lineHeight: "1"
                                }}
                                disabled={exportLoading}
                            >
                                &times;
                            </button>
                        </div>

                        <div className="modal-body">
                            <div className="mb-3">
                                <label className="form-label">Select Inspector</label>
                                <select
                                    className="form-control"
                                    value={selectedInspector}
                                    onChange={(e) => {
                                        setSelectedInspector(e.target.value);
                                        if (e.target.value) {
                                            setSelectedCarpenter("");
                                            setSelectedDirector("");
                                        }
                                    }}
                                    disabled={exportLoading || !(userData.role === 'admin' || userData.role === 'manager' || userData.role === 'inspection' || userData.role === 'coordinator' || userData.role === 'reviewer')}
                                >
                                    <option value="">Choose...</option>
                                    {(userData.role === 'admin' || userData.role === 'manager' || userData.role === 'inspection' || userData.role === 'coordinator' || userData.role === 'reviewer') &&
                                        inspectores.map(i => (
                                            <option key={i.value} value={i.value}>{i.label}</option>
                                        ))
                                    }
                                </select>
                            </div>

                            <div className="mb-3">
                                <label className="form-label">Select Carpenter</label>
                                <select
                                    className="form-control"
                                    value={selectedCarpenter}
                                    onChange={(e) => {
                                        setSelectedCarpenter(e.target.value);
                                        if (e.target.value) {
                                            setSelectedInspector("");
                                            setSelectedDirector("");
                                        }
                                    }}
                                    disabled={exportLoading || !(userData.role === 'admin' || userData.role === 'manager' || userData.role === 'coordinator' || userData.role === 'remedial' || userData.role === 'reviewer')}
                                >
                                    <option value="">Choose...</option>
                                    {(userData.role === 'admin' || userData.role === 'manager' || userData.role === 'coordinator' || userData.role === 'remedial' || userData.role === 'reviewer') &&
                                        carpenters.map(i => (
                                            <option key={i.value} value={i.value}>{i.label}</option>
                                        ))
                                    }
                                </select>
                            </div>

                            <div className="mb-3">
                                <label className="form-label">Select Director</label>
                                <select
                                    className="form-control"
                                    value={selectedDirector}
                                    onChange={(e) => {
                                        setSelectedDirector(e.target.value);
                                        if (e.target.value) {
                                            setSelectedInspector("");
                                            setSelectedCarpenter("");
                                        }
                                    }}
                                    disabled={exportLoading || !(userData.role === 'admin' || userData.role === 'manager' || userData.role === 'coordinator' || userData.role === 'inspection' || userData.role === 'remedial')}
                                >
                                    <option value="">Choose...</option>
                                    {(userData.role === 'admin' || userData.role === 'manager' || userData.role === 'inspection' || userData.role === 'coordinator' || userData.role === 'remedial') &&
                                        directors.map(i => (
                                            <option key={i.value} value={i.value}>{i.label}</option>
                                        ))
                                    }
                                </select>
                            </div>

                            <div className="mb-3">
                                <label className="form-label">Start Date</label>
                                <Flatpickr
                                    className="form-control"
                                    value={startDate}
                                    onChange={([date]) => setStartDate(date)}
                                    options={{
                                        altInput: true,
                                        altFormat: "d/m/Y",
                                        dateFormat: "Y-m-d",
                                        monthSelectorType: "static",
                                        disableMobile: true,
                                    }}
                                    disabled={exportLoading}
                                />
                            </div>

                            <div className="mb-3">
                                <label className="form-label">End Date</label>
                                <Flatpickr
                                    className="form-control"
                                    value={endDate}
                                    onChange={([date]) => setEndDate(date)}
                                    options={{
                                        altInput: true,
                                        altFormat: "d/m/Y",
                                        dateFormat: "Y-m-d",
                                        monthSelectorType: "static",
                                        disableMobile: true,
                                    }}
                                    disabled={exportLoading}
                                />
                            </div>

                            {(selectedInspector || selectedCarpenter || selectedDirector) && startDate && endDate && (() => {
                                const s = new Date(startDate);
                                s.setHours(0, 0, 0, 0);

                                const e = new Date(endDate);
                                e.setHours(23, 59, 59, 999);

                                const filtered = (events || []).filter(ev => {
                                    if (selectedInspector && ev.assignedInspector !== selectedInspector) return false;
                                    if (selectedCarpenter && ev.assignedCarpenter !== selectedCarpenter) return false;
                                    if (selectedDirector && ev.assignedDirector !== selectedDirector) return false;

                                    const evStart = parseDateTime(ev.start);
                                    if (!evStart) return false;
                                    if (evStart < s) return false;
                                    if (evStart > e) return false;

                                    return true;
                                });

                                return filtered.length === 0 ? (
                                    <div className="alert alert-warning text-center">
                                        No events in this range.
                                    </div>
                                ) : null;
                            })()}
                        </div>

                        <div className="modal-footer" style={{ justifyContent: "flex-end" }}>
                            <button
                                className="btn btn-secondary me-2"
                                onClick={() => setShowExportModal(false)}
                                disabled={exportLoading}
                            >
                                Cancel
                            </button>

                            <button
                                className="btn btn-primary d-flex align-items-center"
                                disabled={
                                    (!selectedInspector && !selectedDirector && !selectedCarpenter) ||
                                    !startDate ||
                                    !endDate ||
                                    exportLoading ||
                                    (() => {
                                        const s = new Date(startDate);
                                        s.setHours(0, 0, 0, 0);

                                        const e = new Date(endDate);
                                        e.setHours(23, 59, 59, 999);

                                        const filtered = (events || []).filter(ev => {
                                            if (selectedInspector && ev.assignedInspector !== selectedInspector) return false;
                                            if (selectedCarpenter && ev.assignedCarpenter !== selectedCarpenter) return false;
                                            if (selectedDirector && ev.assignedDirector !== selectedDirector) return false;

                                            const evStart = parseDateTime(ev.start);
                                            if (!evStart) return false;
                                            if (evStart < s) return false;
                                            if (evStart > e) return false;

                                            return true;
                                        });

                                        return filtered.length === 0;
                                    })()
                                }
                                onClick={async () => {
                                    setExportLoading(true);
                                    await handleRunExport(startDate, endDate);
                                    setExportLoading(false);
                                }}
                            >
                                {exportLoading ? (
                                    <>
                                        <Loader className="spin me-2" size={18} />
                                        Exporting...
                                    </>
                                ) : (
                                    " Export PDF"
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}

        </div>
    );
};

const EventModal = ({ user, event, slot, onSave, onCopy, onDelete, onClose, isMobile }) => {
    const userData = useUserRole();

    const [formData, setFormData] = useState({
        title: event?.title || '',
        description: event?.description || '',
        type: event?.category || event?.type || 'general',
        start: event?.start ? dateUtils.toDateTimeLocal(event.start) : slot?.start ? dateUtils.toDateTimeLocal(slot.start) : dateUtils.toDateTimeLocal(new Date()),
        end: event?.end ? dateUtils.toDateTimeLocal(event.end) : slot?.end ? dateUtils.toDateTimeLocal(slot.end) : dateUtils.toDateTimeLocal(addHours(new Date(), 1)),
        buildingEmail: event?.buildingEmail || '',
        location: event?.location || '',
        guests: event?.guests || '',

        buildingAddress: event?.buildingAddress || '',
        buildingCity: event?.buildingCity || '',
        travelDistanceMiles: event?.travelDistanceMiles ?? '',
        travelTimeMinutes: event?.travelTimeMinutes ?? '',
        surveyTimeMinutes: event?.surveyTimeMinutes ?? '',
        totalCommunalDoors: event?.totalCommunalDoors ?? '',
        totalResiDoors: event?.totalResiDoors ?? '',
        preventRescheduling: event?.preventRescheduling || false,
        inspectorName: event?.inspectorName || '',
        directorName: event?.directorName || '',
        carpenterName: event?.carpenterName || '',
        lunchDuration: event?.lunchDuration || '',
        postcode: event?.postcode || '',
        assignedInspector: event?.assignedInspector || '',
        assignedCarpenter: event?.assignedCarpenter || '',
        assignedDirector: event?.assignedDirector || '',
        doorUrl: event?.doorUrl || '',
        materialCost: event?.materialCost || '',
        jobPrice: event?.jobPrice || '',
        labourCost: event?.labourCost || '',

    });
    const [errors, setErrors] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);

    const [copyEventData, setCopyEventData] = useState(null);
    const [copyDate, setCopyDate] = useState("");
    const [isCopying, setIsCopying] = useState(false);

    const [isQuarterlyCopy, setIsQuarterlyCopy] = useState(false);
    const [excludedCopyDates, setExcludedCopyDates] = useState([]);
    const [copiedDates, setCopiedDates] = useState([]);
    const [copyError, setCopyError] = useState("");

    const copyBusyRef = useRef(false);

    const formatCopyDate = (date) => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, "0");
        const day = String(date.getDate()).padStart(2, "0");

        return `${year}-${month}-${day}`;
    };

    const copyDateOptions = (() => {
        if (!copyDate) return [];

        const baseDate = new Date(`${copyDate}T12:00:00`);

        if (
            Number.isNaN(baseDate.getTime()) ||
            formatCopyDate(baseDate) !== copyDate
        ) {
            return [];
        }

        const offsets = isQuarterlyCopy
            ? [3, 6, 9, 12]
            : [0];

        return offsets.map((months) => {
            const date = new Date(baseDate);

            date.setDate(1);
            date.setMonth(baseDate.getMonth() + months);

            const lastDay = new Date(
                date.getFullYear(),
                date.getMonth() + 1,
                0
            ).getDate();

            date.setDate(
                Math.min(baseDate.getDate(), lastDay)
            );

            return {
                value: formatCopyDate(date),
                months,
                label: date.toLocaleDateString(undefined, {
                    weekday: "short",
                    day: "numeric",
                    month: "short",
                    year: "numeric",
                }),
            };
        });
    })();

    const selectedCopyDates = copyDateOptions
        .map((option) => option.value)
        .filter(
            (date) =>
                !excludedCopyDates.includes(date) &&
                !copiedDates.includes(date)
        );


    const getTomorrowDate = () => {
        const tomorrow = new Date();
        tomorrow.setDate(tomorrow.getDate() + 1);

        const year = tomorrow.getFullYear();
        const month = String(tomorrow.getMonth() + 1).padStart(2, "0");
        const day = String(tomorrow.getDate()).padStart(2, "0");

        return `${year}-${month}-${day}`;
    };

    const copyScheduleLocked =
        isCopying || copiedDates.length > 0;

    const canSubmitCopy =
        selectedCopyDates.length > 0 &&
        selectedCopyDates.every(
            (date) => date >= getTomorrowDate()
        );

    const resetCopyModal = () => {
        setCopyEventData(null);
        setCopyDate("");
        setIsQuarterlyCopy(false);
        setExcludedCopyDates([]);
        setCopiedDates([]);
        setCopyError("");
    };

    const handleCopySelectedDates = async () => {
        if (
            copyBusyRef.current ||
            !copyEventData ||
            !canSubmitCopy
        ) {
            return;
        }

        copyBusyRef.current = true;
        setIsCopying(true);
        setCopyError("");

        try {
            const result = await onCopy(
                copyEventData,
                selectedCopyDates
            );

            if (
                !Array.isArray(result?.created) ||
                !Array.isArray(result?.failed)
            ) {
                throw new Error(
                    "The copy callback must return { created, failed }."
                );
            }

            const successfulDates = result.created.map(
                (item) => item.date
            );

            setCopiedDates((previous) => [
                ...new Set([...previous, ...successfulDates]),
            ]);

            if (result.failed.length > 0) {
                const details = result.failed
                    .map((item) => `${item.date}: ${item.error}`)
                    .join("\n");

                setCopyError(
                    `${result.created.length} copied; ` +
                    `${result.failed.length} failed.\n` +
                    `Retry will skip successful copies.\n\n` +
                    details
                );

                toast.error(
                    `${result.failed.length} event copies failed.`
                );

                return;
            }

            const total =
                copiedDates.length + successfulDates.length;

            toast.success(
                total === 1
                    ? "Event copied successfully."
                    : `${total} event copies created successfully.`
            );

            resetCopyModal();
        } catch (error) {
            console.error("Copy event error:", error);

            const message =
                error?.message || "Failed to copy event.";

            setCopyError(message);
            toast.error(message);
        } finally {
            copyBusyRef.current = false;
            setIsCopying(false);
        }
    };

    const handleOpenCopy = () => {
        if (!event) return;

        setCopyEventData(event);

        const start = new Date(event.start);

        const year = start.getFullYear();
        const month = String(start.getMonth() + 1).padStart(2, "0");
        const day = String(start.getDate()).padStart(2, "0");

        setCopyDate(`${year}-${month}-${day}`);
    };

    const closeCopyModal = () => {
        if (copyBusyRef.current || isCopying) return;

        resetCopyModal();
    };

    const getEndTravelTime = () => {
        if (!formData.start) return '';
        const startDate = new Date(formData.start);
        const travelMinutes = parseInt(formData.travelTimeMinutes) || 0;
        const endTravelDate = new Date(startDate.getTime() + travelMinutes * 60000);
        return dateUtils.toDateTimeLocal(endTravelDate);
    };

    const getStartInspectionTime = () => {
        return getEndTravelTime();
    };


    const [inspectores, setInspectores] = useState([]);
    const [carpenters, setCarpenters] = useState([]);
    const [directors, setDirectors] = useState([]);

    const [assignedInspector, setAssignedInspector] = useState(
        event?.assignedInspector
            ? { value: event.assignedInspector, label: event.inspectorName }
            : null
    );

    const [assignedCarpenter, setAssignedCarpenter] = useState(
        event?.assignedCarpenter
            ? { value: event.assignedCarpenter, label: event.carpenterName }
            : null
    );

    const [assignedDirector, setAssignedDirector] = useState(
        event?.assignedDirector
            ? { value: event.assignedDirector, label: event.directorName }
            : null
    );

    const handleRedFocus = (e) => {
        e.target.style.outline = 'none';
        e.target.style.boxShadow = '0 0 0 0.25rem rgba(220, 53, 69, 0.25)';
        e.target.style.borderColor = '#dc3545';
    };

    const handleRedBlur = (e) => {
        e.target.style.boxShadow = '';
        e.target.style.borderColor = '';
    };

    const selectStyles = {
        control: (provided, { isFocused }) => ({
            ...provided,
            borderRadius: "0.375rem",
            padding: "0 0.5rem",
            borderColor: isFocused ? "#dc3545" : "#ced4da",
            boxShadow: isFocused ? "0 0 0 0.2rem rgba(220,53,69,.25)" : "none",
            minHeight: "38px",
            height: "38px",
            outline: "none",
            "&:hover": { borderColor: "#dc3545" },
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            cursor: 'pointer',
        }),
        menu: (provided) => ({
            ...provided,
            width: "260px",
        }),
        option: (provided, { isFocused, isSelected }) => ({
            ...provided,
            backgroundColor: isSelected
                ? "#dc3545"
                : isFocused
                    ? "#ffe5e5"
                    : "white",
            color: isSelected ? "white" : "#333",
            cursor: "pointer",
            minHeight: "38px",
            display: "flex",
            alignItems: "center",
            "&:active": { backgroundColor: "#dc3545" },
        }),
        singleValue: provided => ({
            ...provided,
            color: "#282828ff",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            fontWeight: "bold",
        }),
        placeholder: provided => ({
            ...provided,
            color: "#282828ff",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
        }),
        dropdownIndicator: provided => ({
            ...provided,
            color: "#a71d2a",
            "&:hover": { color: "#dc3545" },
        }),
        clearIndicator: provided => ({
            ...provided,
            color: "#a71d2a",
            "&:hover": { color: "#dc3545" },
        }),
        menuPortal: base => ({ ...base, zIndex: 9999 }),
    };

    function toTitleCase(str) {
        return str
            .toLowerCase()
            .split(" ")
            .map(word => word.charAt(0).toUpperCase() + word.slice(1))
            .join(" ");
    }

    const fetchInspectores = async () => {
        try {
            const q = query(collection(db, "users"), where("user_role", "==", "inspector"));
            const snapshot = await getDocs(q);
            const list = snapshot.docs
                .map(doc => ({ value: doc.id, label: doc.data().name }))
                .filter(u => u.label);
            setInspectores(list);
        } catch (err) {
            console.error("Error fetching inspectores:", err);
        }
    };

    const fetchCarpenters = async () => {
        try {
            const q = query(collection(db, "users"), where("user_role", "==", "carpenter"));
            const snapshot = await getDocs(q);
            const list = snapshot.docs
                .map(doc => ({ value: doc.id, label: doc.data().name }))
                .filter(u => u.label);
            setCarpenters(list);
        } catch (err) {
            console.error("Error fetching carpenters:", err);
        }
    };

    const fetchDirectors = async () => {
        try {
            const q = query(collection(db, "users"), where("user_role", "==", "director"));
            const snapshot = await getDocs(q);
            const list = snapshot.docs
                .map(doc => ({ value: doc.id, label: doc.data().name }))
                .filter(u => u.label);
            setDirectors(list);
        } catch (err) {
            console.error("Error fetching directors:", err);
        }
    };

    useEffect(() => {
        if (!userData.ready) return;

        fetchInspectores();
        fetchCarpenters();
        fetchDirectors();
    }, [userData.ready, userData]);


    const validateForm = () => {
        const newErrors = {};
        if (!formData.start) newErrors.start = 'Start time is required';
        if (!formData.end) newErrors.end = 'End time is required';
        if (!formData.buildingEmail.trim()) newErrors.buildingEmail = 'Building email is required';
        const startDate = dateUtils.fromDateTimeLocal(formData.start);
        const endDate = dateUtils.fromDateTimeLocal(formData.end);
        if (startDate >= endDate) newErrors.end = 'End time must be after start time';
        if (!formData.buildingAddress.trim()) newErrors.buildingAddress = 'Building address is required';
        if (!formData.buildingCity.trim()) newErrors.buildingCity = 'Building city is required';
        if (!formData.inspectorName.trim() && !formData.directorName.trim() && !formData.carpenterName.trim()) {
            newErrors.general = 'At least one of Inspector or Carpenter or Director name is required';
        }
        if (!formData.travelTimeMinutes) newErrors.travelTimeMinutes = 'Travel time is required';
        if (!formData.postcode.trim()) newErrors.postcode = 'Postcode is required';
        if (!formData.totalCommunalDoors) newErrors.totalCommunalDoors = 'Communal doors are required';
        if (!formData.totalResiDoors) newErrors.totalResiDoors = 'Residential doors are required';


        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    <Button
        variant="primary"
        disabled={!copyDate || isCopying}
        onClick={async () => {
            try {
                setIsCopying(true);

                await onCopy(
                    copyEventData,
                    copyDate
                );

                setTimeout(() => {
                    setCopyEventData(null);
                    setCopyDate("");
                }, 1200);

            } catch (error) {
                console.error(
                    "Copy event error:",
                    error
                );
            } finally {
                setIsCopying(false);
            }
        }}
    >
        {isCopying ? (
            <>
                <span
                    className="spinner-border spinner-border-sm me-2"
                    role="status"
                />
                Copying...
            </>
        ) : (
            "Create Copy"
        )}
    </Button>

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (formData.inspectorName.trim() && formData.directorName.trim() && formData.carpenterName.trim()) {
            setErrors(prev => ({ ...prev, general: 'Please select only one: Inspector or Carpenter' }));
            return;
        }

        if (!validateForm() || isSubmitting) return;
        setIsSubmitting(true);

        try {
            const resolvedInspector = assignedInspector || null;
            const resolvedCarpenter = assignedCarpenter || null;
            const resolvedDirector = assignedDirector || null;

            const eventData = {
                ...formData,
                inspectorName: resolvedInspector?.label || '',
                assignedInspector: resolvedInspector?.value || null,
                carpenterName: resolvedCarpenter?.label || '',
                assignedCarpenter: resolvedCarpenter?.value || null,
                directorName: resolvedDirector?.label || '',
                assignedDirector: resolvedDirector?.value || null,
                start: dateUtils.fromDateTimeLocal(formData.start),
                end: dateUtils.fromDateTimeLocal(formData.end),
                endTravelTime: dateUtils.fromDateTimeLocal(getEndTravelTime()),
                startInspectionTime: dateUtils.fromDateTimeLocal(getStartInspectionTime()),
                category: formData.type
            };

            await onSave(eventData);
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleFieldChange = (field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));
        if (errors[field]) setErrors(prev => ({ ...prev, [field]: '' }));
    };

    useEffect(() => {
        const handleKeyDown = (e) => {
            if (e.key === 'Escape') onClose();
        };
        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [onClose]);

    return (
        <div className="modal fade show d-block event-modal-overlay" tabIndex="-1">
            <div className={`modal-dialog ${isMobile ? 'modal-fullscreen' : 'modal-dialog-centered modal-lg'}`}>
                <div className="modal-content event-modal-content" >
                    <div className="modal-header">
                        <h5 className="modal-title">{event?.id ? 'Edit Event' : 'Create Event'}</h5>
                        <button type="button" className="btn-close" onClick={onClose} aria-label="Close"></button>
                    </div>
                    <div
                        className="modal-body"
                        style={{
                            padding: "16px",
                            overflowX: "hidden",
                        }}
                    >
                        <form onSubmit={handleSubmit}>
                            <div className="row g-3 mb-3">
                                <div className="col-12 col-md-6 col-xl">
                                    <label
                                        className="form-label"
                                        style={{
                                            display: "block",
                                            minHeight: "24px",
                                            marginBottom: "6px",
                                        }}
                                    >
                                        Event Title
                                    </label>

                                    <input
                                        type="text"
                                        className={`form-control ${errors.title ? "is-invalid" : ""
                                            }`}
                                        placeholder="Event title"
                                        value={formData.title}
                                        onChange={(e) =>
                                            handleFieldChange("title", e.target.value)
                                        }
                                        maxLength={100}
                                        onFocus={handleRedFocus}
                                        onBlur={handleRedBlur}
                                    />

                                    {errors.title && (
                                        <div className="invalid-feedback">
                                            {errors.title}
                                        </div>
                                    )}

                                    <div
                                        style={{
                                            minHeight: "20px",
                                            marginTop: "5px",
                                            fontSize: "12px",
                                        }}
                                    >
                                        {Number(formData.lunchDuration) > 0 ? (
                                            <span
                                                className="badge"
                                                style={{
                                                    backgroundColor: "#FFD700",
                                                    color: "#000",
                                                    padding: "3px 7px",
                                                }}
                                            >
                                                Lunch time includes
                                            </span>
                                        ) : (
                                            <span className="text-muted">
                                                No lunch time scheduled
                                            </span>
                                        )}
                                    </div>
                                </div>

                                <div className="col-12 col-md-6 col-xl">
                                    <label className="form-label">
                                        Client Email *
                                    </label>

                                    <input
                                        type="email"
                                        className={`form-control ${errors.buildingEmail
                                            ? "is-invalid"
                                            : ""
                                            }`}
                                        placeholder="Add Client Email"
                                        value={formData.buildingEmail}
                                        onChange={(e) =>
                                            handleFieldChange(
                                                "buildingEmail",
                                                e.target.value
                                            )
                                        }
                                        maxLength={200}
                                        onFocus={handleRedFocus}
                                        onBlur={handleRedBlur}
                                    />

                                    {errors.buildingEmail && (
                                        <div className="invalid-feedback">
                                            {errors.buildingEmail}
                                        </div>
                                    )}
                                </div>

                                <div className="col-12 col-md-6 col-xl">
                                    <label className="form-label">
                                        Inspector Name
                                    </label>

                                    <Select
                                        options={
                                            userData.role === "admin" ||
                                                userData.role === "manager" ||
                                                userData.role === "inspection" ||
                                                userData.role === "coordinator"
                                                ? inspectores
                                                : []
                                        }
                                        isDisabled={
                                            !(
                                                userData.role === "admin" ||
                                                userData.role === "manager" ||
                                                userData.role === "inspection" ||
                                                userData.role === "coordinator"
                                            )
                                        }
                                        value={(() => {
                                            if (
                                                !(
                                                    userData.role === "admin" ||
                                                    userData.role === "manager" ||
                                                    userData.role === "inspection" ||
                                                    userData.role === "coordinator"
                                                )
                                            )
                                                return null;

                                            const val =
                                                assignedInspector ||
                                                (formData.assignedInspector
                                                    ? inspectores?.find(
                                                        (i) =>
                                                            i.value ===
                                                            formData.assignedInspector
                                                    ) || null
                                                    : null);

                                            return val
                                                ? {
                                                    ...val,
                                                    label: toTitleCase(
                                                        val.label
                                                    ),
                                                }
                                                : null;
                                        })()}
                                        onChange={(value) => {
                                            setAssignedInspector(value);
                                            setAssignedCarpenter(null);
                                            setAssignedDirector(null);

                                            if (value) {
                                                setErrors((prev) => ({
                                                    ...prev,
                                                    general: "",
                                                }));
                                            }

                                            setFormData((prev) => ({
                                                ...prev,
                                                inspectorName:
                                                    value?.label || "",
                                                assignedInspector:
                                                    value?.value || null,
                                                carpenterName: "",
                                                assignedCarpenter: null,
                                                directorName: "",
                                                assignedDirector: null,
                                            }));
                                        }}
                                        placeholder="Select inspector"
                                        isClearable
                                        isSearchable
                                        menuPlacement="auto"
                                        menuPosition="fixed"
                                        menuPortalTarget={document.body}
                                        styles={selectStyles}
                                    />

                                    {errors.general && (
                                        <div
                                            style={{
                                                color: "#dc3545",
                                                fontSize: "0.875em",
                                                marginTop: "4px",
                                            }}
                                        >
                                            {errors.general}
                                        </div>
                                    )}
                                </div>

                                <div className="col-12 col-md-6 col-xl">
                                    <label className="form-label">
                                        Carpenter Name
                                    </label>

                                    <Select
                                        options={
                                            userData.role === "admin" ||
                                                userData.role === "manager" ||
                                                userData.role === "remedial" ||
                                                userData.role === "coordinator"
                                                ? carpenters
                                                : []
                                        }
                                        isDisabled={
                                            !(
                                                userData.role === "admin" ||
                                                userData.role === "manager" ||
                                                userData.role === "remedial" ||
                                                userData.role === "coordinator"
                                            )
                                        }
                                        value={(() => {
                                            if (
                                                !(
                                                    userData.role === "admin" ||
                                                    userData.role === "manager" ||
                                                    userData.role === "remedial" ||
                                                    userData.role === "coordinator"
                                                )
                                            )
                                                return null;

                                            const val =
                                                assignedCarpenter ||
                                                (formData.assignedCarpenter
                                                    ? carpenters?.find(
                                                        (i) =>
                                                            i.value ===
                                                            formData.assignedCarpenter
                                                    ) || null
                                                    : null);

                                            return val
                                                ? {
                                                    ...val,
                                                    label: toTitleCase(
                                                        val.label
                                                    ),
                                                }
                                                : null;
                                        })()}
                                        onChange={(value) => {
                                            setAssignedCarpenter(value);
                                            setAssignedInspector(null);
                                            setAssignedDirector(null);

                                            if (value) {
                                                setErrors((prev) => ({
                                                    ...prev,
                                                    general: "",
                                                }));
                                            }

                                            setFormData((prev) => ({
                                                ...prev,
                                                carpenterName:
                                                    value?.label || "",
                                                assignedCarpenter:
                                                    value?.value || null,
                                                inspectorName: "",
                                                assignedInspector: null,
                                                directorName: "",
                                                assignedDirector: null,
                                            }));
                                        }}
                                        placeholder="Select carpenter"
                                        isClearable
                                        isSearchable
                                        menuPlacement="auto"
                                        menuPosition="fixed"
                                        menuPortalTarget={document.body}
                                        styles={selectStyles}
                                    />
                                </div>

                                <div className="col-12 col-md-6 col-xl">
                                    <label className="form-label">
                                        Director Name
                                    </label>

                                    <Select
                                        options={
                                            userData.role === "admin" ||
                                                userData.role === "manager" ||
                                                userData.role === "inspection" ||
                                                userData.role === "remedial" ||
                                                userData.role === "coordinator"
                                                ? directors
                                                : []
                                        }
                                        isDisabled={
                                            !(
                                                userData.role === "admin" ||
                                                userData.role === "manager" ||
                                                userData.role === "inspection" ||
                                                userData.role === "remedial" ||
                                                userData.role === "coordinator"
                                            )
                                        }
                                        value={(() => {
                                            if (
                                                !(
                                                    userData.role === "admin" ||
                                                    userData.role === "manager" ||
                                                    userData.role === "inspection" ||
                                                    userData.role === "remedial" ||
                                                    userData.role === "coordinator"
                                                )
                                            )
                                                return null;

                                            const val =
                                                assignedDirector ||
                                                (formData.assignedDirector
                                                    ? directors?.find(
                                                        (i) =>
                                                            i.value ===
                                                            formData.assignedDirector
                                                    ) || null
                                                    : null);

                                            return val
                                                ? {
                                                    ...val,
                                                    label: toTitleCase(
                                                        val.label
                                                    ),
                                                }
                                                : null;
                                        })()}
                                        onChange={(value) => {
                                            setAssignedDirector(value);
                                            setAssignedInspector(null);
                                            setAssignedCarpenter(null);

                                            if (value) {
                                                setErrors((prev) => ({
                                                    ...prev,
                                                    general: "",
                                                }));
                                            }

                                            setFormData((prev) => ({
                                                ...prev,
                                                directorName:
                                                    value?.label || "",
                                                assignedDirector:
                                                    value?.value || null,
                                                inspectorName: "",
                                                assignedInspector: null,
                                                carpenterName: "",
                                                assignedCarpenter: null,
                                            }));
                                        }}
                                        placeholder="Select director"
                                        isClearable
                                        isSearchable
                                        menuPlacement="auto"
                                        menuPosition="fixed"
                                        menuPortalTarget={document.body}
                                        styles={selectStyles}
                                    />
                                </div>
                            </div>

                            <div className="row g-3 mb-3">
                                <div className="col-12 col-sm-6 col-lg-3">
                                    <label className="form-label">
                                        Start Travel Time *
                                    </label>

                                    <input
                                        type="datetime-local"
                                        className={`form-control ${errors.start ? "is-invalid" : ""
                                            }`}
                                        value={formData.start}
                                        onChange={(e) =>
                                            handleFieldChange(
                                                "start",
                                                e.target.value
                                            )
                                        }
                                        onFocus={handleRedFocus}
                                        onBlur={handleRedBlur}
                                    />

                                    {errors.start && (
                                        <div className="invalid-feedback">
                                            {errors.start}
                                        </div>
                                    )}
                                </div>

                                <div className="col-12 col-sm-6 col-lg-3">
                                    <label className="form-label">
                                        End Travel Time
                                    </label>

                                    <input
                                        type="datetime-local"
                                        className="form-control bg-light"
                                        value={getEndTravelTime()}
                                        readOnly
                                        onFocus={handleRedFocus}
                                        onBlur={handleRedBlur}
                                    />
                                </div>

                                <div className="col-12 col-sm-6 col-lg-3">
                                    <label className="form-label">
                                        Start Inspection Time
                                    </label>

                                    <input
                                        type="datetime-local"
                                        className="form-control bg-light"
                                        value={getStartInspectionTime()}
                                        readOnly
                                        onFocus={handleRedFocus}
                                        onBlur={handleRedBlur}
                                    />
                                </div>

                                <div className="col-12 col-sm-6 col-lg-3">
                                    <label className="form-label">
                                        End Inspection Time *
                                    </label>

                                    <input
                                        type="datetime-local"
                                        className={`form-control ${errors.end ? "is-invalid" : ""
                                            }`}
                                        value={formData.end}
                                        onChange={(e) =>
                                            handleFieldChange(
                                                "end",
                                                e.target.value
                                            )
                                        }
                                        onFocus={handleRedFocus}
                                        onBlur={handleRedBlur}
                                    />

                                    {errors.end && (
                                        <div className="invalid-feedback">
                                            {errors.end}
                                        </div>
                                    )}
                                </div>
                            </div>

                            <div className="mb-3">
                                <label className="form-label fw-semibold">Event Type *</label>
                                <div className="event-type-grid">
                                    {Object.entries(eventColors).map(([type, color]) => (
                                        <div key={type} className="event-type-item">
                                            <input
                                                className="form-check-input"
                                                type="radio"
                                                name="eventType"
                                                id={`type-${type}`}
                                                value={type}
                                                checked={formData.type === type}
                                                onChange={(e) => handleFieldChange('type', e.target.value)}
                                            />
                                            <label
                                                className="event-type-label"
                                                htmlFor={`type-${type}`}
                                                style={{
                                                    backgroundColor: formData.type === type ? color.bg : 'transparent',
                                                    borderColor: formData.type === type ? color.border : '#dee2e6',
                                                    color: formData.type === type ? color.text : '#000',
                                                }}
                                            >
                                                <span
                                                    className="color-indicator"
                                                    style={{
                                                        backgroundColor: color.bg,
                                                        border: `2px solid ${color.border}`
                                                    }}
                                                ></span>
                                                <span style={{ fontSize: '16px', }}>{color.label}</span>
                                            </label>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            <div className="row g-3 mb-3">
                                <div className="col-12 col-sm-6 col-lg-3">
                                    <label className="form-label">
                                        Building Address *
                                    </label>

                                    <input
                                        type="text"
                                        className={`form-control ${errors.buildingAddress
                                            ? "is-invalid"
                                            : ""
                                            }`}
                                        placeholder="Add Building Address"
                                        value={formData.buildingAddress}
                                        onChange={(e) =>
                                            handleFieldChange(
                                                "buildingAddress",
                                                e.target.value
                                            )
                                        }
                                        maxLength={200}
                                        onFocus={handleRedFocus}
                                        onBlur={handleRedBlur}
                                    />

                                    {errors.buildingAddress && (
                                        <div className="invalid-feedback">
                                            {errors.buildingAddress}
                                        </div>
                                    )}
                                </div>

                                <div className="col-12 col-sm-6 col-lg-3">
                                    <label className="form-label">
                                        Building City *
                                    </label>

                                    <input
                                        type="text"
                                        className={`form-control ${errors.buildingCity
                                            ? "is-invalid"
                                            : ""
                                            }`}
                                        placeholder="Add Building City"
                                        value={formData.buildingCity}
                                        onChange={(e) =>
                                            handleFieldChange(
                                                "buildingCity",
                                                e.target.value
                                            )
                                        }
                                        maxLength={200}
                                        onFocus={handleRedFocus}
                                        onBlur={handleRedBlur}
                                    />

                                    {errors.buildingCity && (
                                        <div className="invalid-feedback">
                                            {errors.buildingCity}
                                        </div>
                                    )}
                                </div>

                                <div className="col-12 col-sm-6 col-lg-3">
                                    <label className="form-label">
                                        Communal Doors *
                                    </label>

                                    <input
                                        type="number"
                                        className={`form-control ${errors.totalCommunalDoors
                                            ? "is-invalid"
                                            : ""
                                            }`}
                                        placeholder="0"
                                        value={formData.totalCommunalDoors}
                                        onChange={(e) =>
                                            handleFieldChange(
                                                "totalCommunalDoors",
                                                e.target.value
                                            )
                                        }
                                        min="0"
                                        onFocus={handleRedFocus}
                                        onBlur={handleRedBlur}
                                    />

                                    {errors.totalCommunalDoors && (
                                        <div className="invalid-feedback">
                                            {errors.totalCommunalDoors}
                                        </div>
                                    )}
                                </div>

                                <div className="col-12 col-sm-6 col-lg-3">
                                    <label className="form-label">
                                        Residential Doors *
                                    </label>

                                    <input
                                        type="number"
                                        className={`form-control ${errors.totalResiDoors
                                            ? "is-invalid"
                                            : ""
                                            }`}
                                        placeholder="0"
                                        value={formData.totalResiDoors}
                                        onChange={(e) =>
                                            handleFieldChange(
                                                "totalResiDoors",
                                                e.target.value
                                            )
                                        }
                                        min="0"
                                        onFocus={handleRedFocus}
                                        onBlur={handleRedBlur}
                                    />

                                    {errors.totalResiDoors && (
                                        <div className="invalid-feedback">
                                            {errors.totalResiDoors}
                                        </div>
                                    )}
                                </div>
                            </div>

                            <div className="row g-3">
                                <div className="col-12 col-lg-6">
                                    <div className="row g-3">
                                        <div className="col-12 col-sm-6">
                                            <label className="form-label">
                                                PostCode *
                                            </label>

                                            <input
                                                type="text"
                                                className={`form-control ${errors.postcode
                                                    ? "is-invalid"
                                                    : ""
                                                    }`}
                                                placeholder="Add PostCode"
                                                value={formData.postcode}
                                                onChange={(e) =>
                                                    handleFieldChange(
                                                        "postcode",
                                                        e.target.value
                                                    )
                                                }
                                                maxLength={200}
                                                onFocus={handleRedFocus}
                                                onBlur={handleRedBlur}
                                            />

                                            {errors.postcode && (
                                                <div className="invalid-feedback">
                                                    {errors.postcode}
                                                </div>
                                            )}
                                        </div>

                                        <div className="col-12 col-sm-6">
                                            <label className="form-label">
                                                Travel Time *
                                            </label>

                                            <input
                                                type="number"
                                                className={`form-control ${errors.travelTimeMinutes
                                                    ? "is-invalid"
                                                    : ""
                                                    }`}
                                                placeholder="0"
                                                value={
                                                    formData.travelTimeMinutes
                                                }
                                                onChange={(e) =>
                                                    handleFieldChange(
                                                        "travelTimeMinutes",
                                                        e.target.value
                                                    )
                                                }
                                                min="0"
                                                onFocus={handleRedFocus}
                                                onBlur={handleRedBlur}
                                            />

                                            {errors.travelTimeMinutes && (
                                                <div className="invalid-feedback">
                                                    {
                                                        errors.travelTimeMinutes
                                                    }
                                                </div>
                                            )}
                                        </div>

                                        <div className="col-12 col-sm-6">
                                            <label className="form-label">
                                                Lunch Time
                                            </label>

                                            <input
                                                type="number"
                                                className={`form-control ${errors.lunchDuration
                                                    ? "is-invalid"
                                                    : ""
                                                    }`}
                                                placeholder="0"
                                                value={formData.lunchDuration}
                                                onChange={(e) =>
                                                    handleFieldChange(
                                                        "lunchDuration",
                                                        e.target.value
                                                    )
                                                }
                                                min="0"
                                                onFocus={handleRedFocus}
                                                onBlur={handleRedBlur}
                                            />

                                            {errors.lunchDuration && (
                                                <div className="invalid-feedback">
                                                    {errors.lunchDuration}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                <div className="col-12 col-lg-6">
                                    <label className="form-label">
                                        Description
                                    </label>

                                    <textarea
                                        className="form-control"
                                        placeholder="Add description..."
                                        value={formData.description}
                                        onChange={(e) =>
                                            handleFieldChange(
                                                "description",
                                                e.target.value
                                            )
                                        }
                                        maxLength={500}
                                        onFocus={handleRedFocus}
                                        onBlur={handleRedBlur}
                                        rows={5}
                                        style={{
                                            resize: "none",
                                            minHeight: "120px",
                                        }}
                                    />
                                </div>
                            </div>

                            <div
                                className="modal-footer"
                                style={{
                                    padding: "14px 0 0 0",
                                    marginTop: "12px",
                                    borderTop: "1px solid #e9ecef",
                                    display: "flex",
                                    flexWrap: "wrap",
                                    alignItems: "center",
                                    justifyContent: "space-between",
                                    gap: "12px",
                                }}
                            >
                                <div
                                    className="d-flex align-items-center gap-2"
                                    style={{
                                        flex: "1 1 360px",
                                        minWidth: 0,
                                        maxWidth: "520px",
                                    }}
                                >
                                    <input
                                        type="text"
                                        className="form-control"
                                        placeholder="Enter Door URL"
                                        value={formData.doorUrl}
                                        onChange={(e) =>
                                            setFormData({
                                                ...formData,
                                                doorUrl: e.target.value,
                                            })
                                        }
                                        onFocus={handleRedFocus}
                                        onBlur={handleRedBlur}
                                        style={{
                                            flex: 1,
                                            minWidth: 0,
                                        }}
                                    />

                                    <button
                                        type="button"
                                        className="btn btn-primary"
                                        disabled={!formData.doorUrl}
                                        onClick={() =>
                                            window.open(
                                                formData.doorUrl,
                                                "_blank",
                                                "noopener,noreferrer"
                                            )
                                        }
                                        style={{
                                            minWidth: "90px",
                                            flexShrink: 0,
                                            whiteSpace: "nowrap",
                                        }}
                                    >
                                        View
                                    </button>
                                </div>

                                {(userData.role === "admin" ||
                                    userData.role === "manager" ||
                                    userData.role === "inspection" ||
                                    userData.role === "coordinator" ||
                                    userData.role === "remedial") && (
                                        <div
                                            className="event-modal-actions"
                                            style={{
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "flex-end",
                                                gap: "8px",
                                                marginLeft: "auto",
                                            }}
                                        >
                                            {onDelete && (
                                                <button
                                                    type="button"
                                                    className="btn btn-outline-primary"
                                                    onClick={() => handleOpenCopy(event)}
                                                >
                                                    Make Copy
                                                </button>
                                            )}

                                            {onDelete && (
                                                <button
                                                    type="button"
                                                    className="btn btn-outline-danger"
                                                    onClick={onDelete}
                                                >
                                                    Delete
                                                </button>
                                            )}

                                            <button
                                                type="button"
                                                className="btn btn-outline-secondary"
                                                onClick={onClose}
                                            >
                                                Cancel
                                            </button>

                                            <button
                                                type="submit"
                                                className="btn btn-primary"
                                                disabled={isSubmitting}
                                            >
                                                {isSubmitting ? (
                                                    <>
                                                        <span
                                                            className="spinner-border spinner-border-sm me-2"
                                                            role="status"
                                                        />
                                                        Saving...
                                                    </>
                                                ) : event?.id ? (
                                                    "Update"
                                                ) : (
                                                    "Create"
                                                )}
                                            </button>
                                        </div>
                                    )}

                                <style>
                                    {`
                                    @media (max-width: 575px) {
                                        .modal-footer {
                                            gap: 10px !important;
                                        }

                                        .event-modal-actions {
                                            width: 100%;
                                            margin-left: 0 !important;
                                            display: grid !important;
                                            grid-template-columns: repeat(3, minmax(0, 1fr));
                                            gap: 8px !important;
                                        }

                                        .event-modal-actions .btn {
                                            width: 100%;
                                            min-width: 0;
                                            padding-left: 6px;
                                            padding-right: 6px;
                                            white-space: nowrap;
                                        }
                                    }

                                    @media (max-width: 360px) {
                                        .event-modal-actions {
                                            grid-template-columns: 1fr 1fr !important;
                                        }

                                        .event-modal-actions .btn:last-child {
                                            grid-column: 1 / -1;
                                        }
                                    }
                                `}
                                </style>
                            </div>

                            <style>
                                {`
                                    @media (max-width: 575px) {
                                        .modal-body {
                                            padding: 12px !important;
                                        }

                                        .modal-body .form-label {
                                            font-size: 13px;
                                        }

                                        .modal-body .form-control,
                                        .modal-body .form-select {
                                            min-height: 42px;
                                            font-size: 14px;
                                        }

                                        .modal-footer {
                                            flex-direction: column !important;
                                            align-items: stretch !important;
                                        }

                                        .modal-footer > div {
                                            width: 100%;
                                            max-width: 100% !important;
                                        }

                                        .modal-footer .btn {
                                            min-height: 42px;
                                        }

                                        .modal-footer > div:last-child {
                                            display: grid !important;
                                            grid-template-columns: repeat(3, 1fr);
                                            width: 100%;
                                        }
                                    }

                                    @media (max-width: 380px) {
                                        .modal-footer > div:last-child {
                                            grid-template-columns: 1fr;
                                        }
                                    }
                                `}
                            </style>
                        </form>
                    </div>
                </div>
            </div >

            <ReactModal
                isOpen={!!copyEventData}
                onRequestClose={closeCopyModal}
                shouldCloseOnOverlayClick={!isCopying}
                shouldCloseOnEsc={!isCopying}
                aria={{ labelledby: "copy-modal-title" }}
                style={{
                    overlay: {
                        position: "fixed",
                        inset: 0,
                        zIndex: 99999,
                        backgroundColor: "rgba(20, 25, 30, 0.30)",
                        backdropFilter: "blur(6px)",
                        WebkitBackdropFilter: "blur(6px)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        padding: "16px",
                    },

                    content: {
                        position: "relative",
                        inset: "auto",
                        width: "100%",
                        maxWidth: "520px",
                        maxHeight: "calc(100vh - 32px)",
                        overflowY: "auto",
                        margin: 0,
                        padding: 0,
                        border: "none",
                        borderRadius: "14px",
                        backgroundColor: "#fff",
                        boxShadow:
                            "0 20px 60px rgba(0,0,0,0.25), 0 5px 20px rgba(0,0,0,0.12)",
                    },
                }}
            >
                <div
                    style={{
                        padding: "16px 18px",
                        borderBottom: "1px solid #e9ecef",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: "12px",
                    }}
                >
                    <div>
                        <div
                            style={{
                                fontSize: "12px",
                                color: "#6c757d",
                                fontWeight: 600,
                                marginBottom: "2px",
                            }}
                        >
                            Duplicate Event
                        </div>

                        <div
                            id="copy-modal-title"
                            style={{
                                fontSize: "19px",
                                fontWeight: 700,
                                color: "#212529",
                            }}
                        >
                            Make Event Copy
                        </div>
                    </div>

                    <button
                        type="button"
                        className="btn-close"
                        aria-label="Close"
                        disabled={isCopying}
                        onClick={closeCopyModal}
                    />
                </div>

                <div style={{ padding: "18px" }}>
                    <div className="mb-3">
                        <label
                            htmlFor="copy-event-name"
                            className="form-label"
                            style={{
                                fontSize: "13px",
                                fontWeight: 600,
                            }}
                        >
                            Event
                        </label>

                        <input
                            id="copy-event-name"
                            type="text"
                            className="form-control bg-light"
                            value={copyEventData?.title || ""}
                            readOnly
                        />
                    </div>

                    <div className="mb-3">
                        <label
                            htmlFor="copy-event-date"
                            className="form-label"
                            style={{
                                fontSize: "13px",
                                fontWeight: 600,
                            }}
                        >
                            {isQuarterlyCopy
                                ? "Copy to selected dates 3, 6, 9 or 12 months after the starting date."
                                : "Create one copy on the selected date."}
                        </label>

                        <input
                            id="copy-event-date"
                            type="date"
                            className="form-control"
                            value={copyDate}
                            min={getTomorrowDate()}
                            disabled={copyScheduleLocked}
                            onChange={(e) => {
                                setCopyDate(e.target.value);
                                setExcludedCopyDates([]);
                                setCopyError("");
                            }}
                        />

                        {copyDate && copyDate < getTomorrowDate() && (
                            <div
                                className="text-danger mt-1"
                                style={{ fontSize: "12px" }}
                            >
                                Please select tomorrow or a later date.
                            </div>
                        )}
                    </div>

                    <div
                        style={{
                            padding: "14px",
                            border: "1px solid #e9ecef",
                            borderRadius: "10px",
                            backgroundColor: "#f8f9fa",
                        }}
                    >
                        <div
                            className="form-switch"
                            style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "12px",
                                paddingLeft: 0,
                                margin: 0,
                            }}
                        >
                            <input
                                id="quarterly-copy-switch"
                                type="checkbox"
                                role="switch"
                                className="form-check-input copy-quarterly-toggle"
                                checked={isQuarterlyCopy}
                                disabled={copyScheduleLocked}
                                style={{
                                    position: "static",
                                    float: "none",
                                    margin: 0,
                                    width: "42px",
                                    height: "24px",
                                    flex: "0 0 42px",
                                    cursor: copyScheduleLocked
                                        ? "default"
                                        : "pointer",
                                    marginRight: "12px",

                                }}
                                onChange={(e) => {
                                    setIsQuarterlyCopy(e.target.checked);
                                    setExcludedCopyDates([]);
                                    setCopyError("");
                                }}
                            />

                            <div style={{ minWidth: 0 }}>
                                <label
                                    htmlFor="quarterly-copy-switch"
                                    style={{
                                        display: "block",
                                        margin: 0,
                                        fontSize: "13px",
                                        fontWeight: 600,
                                        lineHeight: 1.4,
                                        cursor: copyScheduleLocked
                                            ? "default"
                                            : "pointer",
                                    }}
                                >
                                    Copy quarterly for the next year
                                </label>

                                <div
                                    style={{
                                        marginTop: "4px",
                                        fontSize: "12px",
                                        color: "#6c757d",
                                        lineHeight: 1.4,
                                    }}
                                >
                                    {isQuarterlyCopy
                                        ? "Choose dates 3, 6, 9 or 12 months later."
                                        : "Create one copy on the selected date."}
                                </div>
                            </div>
                        </div>
                    </div>

                    {isQuarterlyCopy && copyDateOptions.length > 0 && (
                        <fieldset
                            style={{
                                border: 0,
                                padding: 0,
                                margin: "16px 0 0",
                                minWidth: 0,
                            }}
                        >
                            <legend
                                style={{
                                    fontSize: "13px",
                                    fontWeight: 600,
                                    marginBottom: "10px",
                                }}
                            >
                                Select dates to copy
                            </legend>

                            <div
                                style={{
                                    display: "grid",
                                    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
                                    gap: "10px",
                                }}
                            >
                                {copyDateOptions.map((option) => {
                                    const alreadyCopied =
                                        copiedDates.includes(option.value);

                                    const checked =
                                        alreadyCopied ||
                                        !excludedCopyDates.includes(option.value);

                                    const disabled =
                                        isCopying || alreadyCopied;

                                    return (
                                        <label
                                            key={option.value}
                                            htmlFor={`copy-date-${option.value}`}
                                            style={{
                                                display: "flex",
                                                alignItems: "center",
                                                gap: "10px",
                                                minWidth: 0,
                                                margin: 0,
                                                padding: "12px",
                                                border: alreadyCopied
                                                    ? "1px solid #b7dfc4"
                                                    : "1px solid #e2e6ea",
                                                borderRadius: "9px",
                                                backgroundColor: alreadyCopied
                                                    ? "#f0fdf4"
                                                    : "#fff",
                                                cursor: disabled
                                                    ? "default"
                                                    : "pointer",
                                            }}
                                        >
                                            <input
                                                id={`copy-date-${option.value}`}
                                                type="checkbox"
                                                className="form-check-input"
                                                checked={checked}
                                                disabled={disabled}
                                                style={{
                                                    position: "static",
                                                    float: "none",
                                                    margin: 0,
                                                    width: "18px",
                                                    height: "18px",
                                                    flex: "0 0 18px",
                                                    alignSelf: "center",
                                                    cursor: disabled
                                                        ? "default"
                                                        : "pointer",
                                                }}
                                                onChange={(e) => {
                                                    const isChecked = e.target.checked;

                                                    setExcludedCopyDates((previous) =>
                                                        isChecked
                                                            ? previous.filter(
                                                                (date) =>
                                                                    date !== option.value
                                                            )
                                                            : [...previous, option.value]
                                                    );
                                                }}
                                            />

                                            <span
                                                style={{
                                                    display: "flex",
                                                    flexDirection: "column",
                                                    gap: "4px",
                                                    minWidth: 0,
                                                }}
                                            >
                                                <span
                                                    style={{
                                                        fontSize: "12px",
                                                        fontWeight: 600,
                                                        color: "#212529",
                                                        lineHeight: 1.4,
                                                        overflowWrap: "anywhere",
                                                    }}
                                                >
                                                    {option.label}
                                                </span>

                                                <span
                                                    style={{
                                                        fontSize: "11px",
                                                        fontWeight: 400,
                                                        lineHeight: 1.3,
                                                        color: alreadyCopied
                                                            ? "#198754"
                                                            : "#6c757d",
                                                    }}
                                                >
                                                    {alreadyCopied
                                                        ? "Copied"
                                                        : option.months === 0
                                                            ? "First date"
                                                            : `+${option.months} months`}
                                                </span>
                                            </span>
                                        </label>
                                    );
                                })}
                            </div>
                        </fieldset>
                    )}

                    <div
                        style={{
                            marginTop: "12px",
                            fontSize: "12px",
                            color: "#6c757d",
                            lineHeight: 1.5,
                        }}
                    >
                        All event details will be copied.
                        Only the dates will change.
                        The original start and end times will stay the same.
                    </div>

                    {copiedDates.length > 0 && (
                        <div
                            className="text-success mt-2"
                            style={{ fontSize: "12px" }}
                        >
                            {copiedDates.length} already copied.
                            Retry will skip these dates.
                        </div>
                    )}

                    {copyError && (
                        <div
                            role="alert"
                            className="alert alert-danger mt-3 mb-0"
                            style={{
                                fontSize: "12px",
                                whiteSpace: "pre-wrap",
                            }}
                        >
                            {copyError}
                        </div>
                    )}
                </div>

                <div
                    style={{
                        padding: "12px 18px",
                        borderTop: "1px solid #e9ecef",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "flex-end",
                        flexWrap: "wrap",
                        gap: "8px",
                    }}
                >
                    <span
                        aria-live="polite"
                        style={{
                            marginRight: "auto",
                            fontSize: "12px",
                            color: "#6c757d",
                        }}
                    >
                        {selectedCopyDates.length}{" "}
                        {selectedCopyDates.length === 1 ? "copy" : "copies"} selected
                    </span>

                    <button
                        type="button"
                        className="btn btn-outline-secondary"
                        disabled={isCopying}
                        onClick={closeCopyModal}
                    >
                        Cancel
                    </button>

                    <Button
                        variant="primary"
                        disabled={!canSubmitCopy || isCopying}
                        onClick={handleCopySelectedDates}
                    >
                        {isCopying ? (
                            <>
                                <span
                                    className="spinner-border spinner-border-sm me-2"
                                    aria-hidden="true"
                                />
                                Copying...
                            </>
                        ) : copiedDates.length > 0 ? (
                            "Retry Selected Dates"
                        ) : selectedCopyDates.length > 1 ? (
                            `Create ${selectedCopyDates.length} Copies`
                        ) : (
                            "Create Copy"
                        )}
                    </Button>
                </div>
            </ReactModal >

            <ToastContainer
                position="top-right"
                autoClose={3000}
                theme="colored"
            />
        </div >
    );
};

export const CalendarEventModal = ({
    directOpen = false,
    directEvent = null,
    directSlot = null,
    directPrefill = null,
    directTicketId = null,
    onScheduled,
    onDirectClose,
}) => {
    const [user] = useAuthState(auth);
    const { addEvent, updateEvent, deleteEvent, setError } = useCalendarEvents(user);

    if (!directOpen) return null;

    const isCreating = !directEvent?.id;

    const eventForModal = directEvent
        ? directEvent
        : directPrefill
            ? { ...directPrefill, id: undefined }
            : null;

    const handleSave = async (eventData) => {
        try {
            setError(null);
            const eventDataWithCategory = {
                ...eventData,
                category: eventData.category || eventData.type,
                ...(directTicketId && isCreating ? { ticket_id: directTicketId } : {}),
            };

            if (!isCreating) {
                const ok = await updateEvent(directEvent.id, eventDataWithCategory);
                if (!ok) throw new Error("The event was not saved. Please try again.");
            } else {
                const newEvent = await addEvent(eventDataWithCategory);
                if (!newEvent?.id) throw new Error("The event was not created. Please try again.");
                if (directTicketId && onScheduled) {
                    onScheduled(newEvent);
                }
            }
            onDirectClose?.();
        } catch (err) {
            setError(err.message);
        }
    };

    const handleDelete = async () => {
        if (!directEvent?.id) return;
        if (!window.confirm("Are you sure you want to delete this event?")) return;
        try {
            setError(null);
            await deleteEvent(directEvent.id, directEvent?.ticket_id);
            onDirectClose?.();
        } catch (err) {
            setError(err.message);
        }
    };

    return (
        <EventModal
            user={user}
            event={eventForModal}
            slot={directSlot}
            onSave={handleSave}
            onDelete={directEvent?.id ? handleDelete : null}
            onClose={onDirectClose}
            isMobile={typeof window !== "undefined" && window.innerWidth < 768}
        />
    );
};

export default CalendarPage;